import React, {
  useEffect,
  useRef,
  useState,
  FC,
  Dispatch,
  SetStateAction,
} from "react";
import { useStore } from "../../store/store";
import fractionToInt from "../../util/fractionToInt";
import { calculatePanelWidth } from "../../util/formulas/calculatePanelWidth";
import { calculatePanelHeight } from "../../util/formulas/calculatePanelHeight";
import calculateCornerPanelWidth from "../../util/formulas/calculateCornerPanelWidth";
import { convertDecimalToFraction } from "../../util/formulas/convertDecimalToFraction";
import { woodImgs } from "../Wood/defaults";
import {
  pocketSliderCalc,
  findHandlePosition,
  findNumPanelsBifold,
  findNumPanelsSlider,
  calculateDLO,
  calculateDLOAlum,
  findNumPanelsSwing,
  frameDimensions,
  findNumPanelsPivot,
  handleImage,
  getScale,
  getGaps,
  findHandlePositionSwing,
  findHandlePositionSwingAlum,
  findHandlePositionSlider,
  pivotBackPlateImage,
  frameDimensionsAlum,
  getGapsAlum,
  panelHeightAlumPixels,
  calcAlumSideliteHeightPixels,
  panelWidthAlumPixels,
  calcSideliteWidthAlumPixels,
  picoShootbolt,
  picoShootboltPanels,
  findOddOddShootboltPositionPicoWindow,
  alumBifoldCylinderImage,
} from "./helpers";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import noAccess from "../../util/noAccess";
import { calculateAlumSideliteHeight } from "../../util/formulas/calculateAlumSideliteHeight";

const StyledDiv = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: 20,
  paddingTop: 0,
});

const cladColors = {
  "Jet Black": "#141615",
  "Carbon Black": "#141615",
  "Espresso Brown": "#3f3931",
  "Sandcastle Tan": "#998d76",
  "Linen White": "#eeeeee",
  "Clear Anodized": "#b0b4b4",
};

const cloudinaryWoodURL =
  "https://res.cloudinary.com/ag-millworks/image/upload/a_90/WoodSpecies";

const cloudinaryWoodURL2 =
  "https://res.cloudinary.com/ag-millworks/image/upload/WoodSpecies";

type FillRectArgs = [number, number, number, number];

type CanvasLinePlot = { x1: number; y1: number; x2: number; y2: number };

type CanvasHeightTextPlot = {
  text: string;
  x: number;
  y: number;
};

type CanvasWidthTextPlot = {
  labelOptions: [string, string];
  width: number | string;
  pixelWidth: number;
  xStart: number;
  yPosTop: number;
  yPosBtm: number;
};

interface CanvasBoxProps {
  canvasWidth: number;
  canvasHeight: number;
  containerWindow?: Window;
  setSize: Dispatch<SetStateAction<{ width: number; height: number }>>;
  dataUrl?: boolean;
}

function sumDigitsFromString(str: string) {
  let sum = 0;
  for (let i = 0; i < str.length; i++) {
    // Attempt to convert the character to a number
    const number = Number(str[i]);

    // Check if the result is a valid number and not NaN (Not a Number)
    if (!isNaN(number)) {
      sum += number;
    }
  }
  return sum;
}

function formatDimStr(whole: string, fraction: string) {
  if (fraction) {
    return whole + " " + fraction + '"';
  }

  return whole + '"';
}

const CanvasBox: FC<CanvasBoxProps> = ({
  canvasWidth,
  canvasHeight,
  containerWindow,
  setSize,
  dataUrl,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRefPortal = useRef<HTMLCanvasElement>(null);
  // bumped on every redraw so image onloads from an older draw don't paint over the new one
  const drawIdRef = useRef(0);

  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    if (containerWindow) {
      const handleResize = () => {
        const newWidth = containerWindow.innerWidth - 70;
        const newHeight = containerWindow.innerHeight - 70;

        setSize({
          width: newWidth,
          height: newHeight,
        });
      };

      containerWindow.addEventListener("resize", handleResize);
      handleResize(); // Initial size update

      return () => {
        containerWindow.removeEventListener("resize", handleResize);
      };
    }
  }, [containerWindow]);

  const clearCanvas = (ctx: React.RefObject<HTMLCanvasElement>) => {
    const canvas = ctx.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const getTextWidth = (ctx: CanvasRenderingContext2D, text: string) => {
    return Math.floor(ctx.measureText(text).width);
  };

  const centerText = (top: number, btm: number) => {
    const diff = top - btm;
    return Math.abs(diff / 2);
  };

  const drawLine = (
    ctx: CanvasRenderingContext2D,
    xStart: number,
    yStart: number,
    xEnd: number,
    yEnd: number,
  ) => {
    ctx.save();
    // ctx.strokeStyle = strokeStyle
    ctx.beginPath();
    ctx.moveTo(xStart, yStart);
    ctx.lineTo(xEnd, yEnd);
    ctx.stroke();
    ctx.restore();
  };

  const drawWidthText = (
    ctx: CanvasRenderingContext2D,
    labelOptions: [string, string],
    width: number | string,
    pixelWidth: number,
    xStart: number,
    yPosTop: number,
    yPosBtm: number,
  ) => {
    ctx.font = font;
    const label = dataUrl ? labelOptions[0] : labelOptions[1];
    const widthText =
      typeof width === "string" ? width : convertDecimalToFraction(width) + '"';

    const topWidthPixels = getTextWidth(ctx, label);
    const btmWidthPixels = getTextWidth(ctx, widthText);

    const center = centerText(topWidthPixels, btmWidthPixels);
    const xPos = xStart - topWidthPixels / 2 + pixelWidth / 2;

    ctx.fillText(label, xPos, yPosTop);
    ctx.fillText(
      widthText,
      btmWidthPixels >= topWidthPixels ? xPos - center : xPos + center,
      yPosBtm,
    );
  };

  const drawHeightText = (
    ctx: CanvasRenderingContext2D,
    heightText: string,
    xPos: number,
    yPos: number,
  ) => {
    const textWidth = getTextWidth(ctx, heightText);
    ctx.save(); // Save current state
    ctx.translate(xPos, yPos + textWidth / 2);
    ctx.rotate(-Math.PI / 2); // Rotate counter-clockwise
    ctx.fillText(heightText, 0, 0); // Draw text
    ctx.restore(); // Restore to previous state
  };

  const doorType = useStore((state) => state.quote.doorType);

  const isCorner = doorType?.includes("Corner");
  const isCombo = doorType?.includes("Combo");
  const isBifold = doorType?.includes("Bi-fold");
  const isBifoldDoor = doorType === "Bi-fold";
  const isBifoldWindow = doorType === "Bi-fold Window";
  const isSlider = doorType === "Slider";
  const isSwing = doorType === "Swing";
  const isPivot = doorType === "Pivot";
  const isFixedPanel = doorType === "Fixed Panel";

  const comboUnitLeft = useStore((state) => state.quote.comboUnitLeft);
  const comboUnitRight = useStore((state) => state.quote.comboUnitRight);

  const material = useStore((state) => state.quote.material);
  const woodSpecies = useStore((state) => state.quote.woodSpecies);

  const sillStyle = useStore((state) => state.quote.sillStyle);

  const isComboSillDifferent = useStore(
    (state) => state.quote.isComboSillDifferent,
  );

  const comboWindowSillStyle = useStore(
    (state) => state.quote.comboWindowSillStyle,
  );

  const stilesAndRails = useStore((state) => state.quote.stilesAndRails);
  const lockRail = useStore((state) => state.quote.lockRail);

  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);

  const cornerWidthRight = useStore((state) => state.quote.cornerWidthRight);
  const cornerWidthFractionRight = useStore(
    (state) => state.quote.cornerWidthFractionRight,
  );

  const comboWindowUnitSize = useStore(
    (state) => state.quote.comboWindowUnitSize,
  );
  const comboWindowUnitSizeFraction = useStore(
    (state) => state.quote.comboWindowUnitSizeFraction,
  );

  const heightSize = useStore((state) => state.quote.height);
  const heightFraction = useStore((state) => state.quote.heightFraction);

  const comboWindowHeight = useStore((state) => state.quote.comboWindowHeight);
  const comboWindowHeightFraction = useStore(
    (state) => state.quote.comboWindowHeightFraction,
  );

  const cornerType = useStore((state) => state.quote.cornerType);
  const cornerConfigRight = useStore((state) => state.quote.cornerConfigRight);
  const comboWindowConfig = useStore((state) => state.quote.comboWindowConfig);

  const netWidth = fractionToInt(unitSize, unitSizeFraction);
  const netHeight = fractionToInt(heightSize, heightFraction);

  const netWidthComboWindow = isCombo
    ? fractionToInt(
        comboWindowUnitSize || "",
        comboWindowUnitSizeFraction || "",
      )
    : 0;
  const netHeightComboWindow = isCombo
    ? fractionToInt(comboWindowHeight || "", comboWindowHeightFraction || "")
    : 0;

  const cornerFrameWidthLeft = useStore(
    (state) => state.quote.cornerFrameWidthLeft,
  );
  const cornerFrameWidthRight = useStore(
    (state) => state.quote.cornerFrameWidthRight,
  );

  const netWidthCornerRight = !cornerWidthRight
    ? 0
    : fractionToInt(cornerWidthRight, cornerWidthFractionRight);

  const exactConfig = useStore((state) => state.quote.exactConfig);
  const primaryDoor = useStore((state) => state.quote.primaryDoor);

  const addDL = useStore((state) => state.quote.addDL);
  const liteConfig = useStore((state) => state.quote.liteConfig);
  const sdl = useStore((state) => state.quote.dlLayout);
  const daylight = useStore((state) => state.quote.daylightOpening);

  const comboMatchHorizontalSdlBars = useStore(
    (state) => state.quote.comboMatchHorizontalSdlBars,
  );

  const dlLayoutSidelite = useStore((state) => state.quote.dlLayoutSidelite);
  const diffSideliteSdl = useStore((state) => state.quote.diffSideliteSdl);
  const stiles = useStore((state) => state.quote.stileCategory);

  const withHandle = useStore((state) => state.quote.withHandle);
  const handleStyle = useStore((state) => state.quote.handleStyle);
  const handleHeight = useStore((state) => state.quote.handleHeight);
  const handleColor = useStore((state) => state.quote.handleColor);
  const secondaryHandle = useStore((state) => state.quote.secondaryHandle);

  const shootBoltColor = useStore((state) => state.quote.shootBoltColor);

  const withExtSbolt = useStore((state) => state.quote.withExtSbolt);

  const hasBluetoothHandle =
    withHandle === "Add Handle Set" && handleStyle === "Smart Lock *"
      ? true
      : isPivot && handleStyle === "Smart Lock *"
        ? true
        : false;
  const isAluminum = material === "All Aluminum";

  const hasHandle =
    doorType?.includes("Window") && !isCombo
      ? false
      : handleStyle === "Handle Pull by Others"
        ? false
        : withHandle === "Add Handle Set" || !withHandle
          ? true
          : false;

  const sliderPocket = useStore((state) => state.quote.sliderPocket);

  const swingPanelWidth = useStore((state) => state.quote.swingPanelWidth);
  const swingPanelFraction = useStore(
    (state) => state.quote.swingPanelFraction,
  );
  const screenType = useStore((state) => state.quote.screenType);
  const addScreen = useStore((state) => state.quote.addScreen);

  const swingDirection = useStore((state) => state.quote.swingDirection);
  const swingType = useStore((state) => state.quote.swingType);
  const sideLite = useStore((state) => state.quote.sideLite);
  const sideLiteWidth = useStore((state) => state.quote.sideLiteWidth);
  const exactConfigImg = useStore((state) => state.quote.exactConfigImg);

  const pivotHardware = useStore((state) => state.quote.pivotHardware);
  const pivotAction = useStore((state) => state.quote.pivotAction);
  const pivotPoint = useStore((state) => state.quote.pivotPoint);
  const pivotBackPlate = useStore((state) => state.quote.pivotBackPlate);
  const pivotBackPlateColor = useStore(
    (state) => state.quote.pivotBackPlateColor,
  );

  const fullboundProfile = useStore((state) => state.quote.fullboundProfile);

  const exteriorCladColor = useStore((state) => state.quote.exteriorCladColor);

  const errors = useStore((state) => state.errors);
  const aluminumColorExterior = useStore(
    (state) => state.quote.aluminumColorExterior,
  );

  const isPico = stiles === "Pico";

  const showExtShootbolts =
    (isBifoldDoor || isBifoldWindow) &&
    (isPico || isAluminum) &&
    (withExtSbolt === "Yes" || !withExtSbolt);

  const PICO_ADD = 0.472441;
  const alumThickFrame = useStore((state) => state.quote.alumThickFrame);

  const equalPanels = useStore((state) => state.quote.equalPanels);

  const errorArray = [errors.widthErr, errors.heightErr];
  const required = [stiles, unitSize, heightSize, exactConfig];

  if (isAluminum) {
    required.shift();
  }

  if (isSwing) {
    required.push(swingDirection, swingPanelWidth);

    errorArray.push(errors.swingWidthErr, errors.sideliteWidthErr);
  }

  if (isPivot) {
    required.push(swingPanelWidth);

    errorArray.push(errors.swingWidthErr, errors.sideliteWidthErr);
  }

  if (material === "All Wood") {
    required.push(woodSpecies);
  }

  if (isCorner) {
    required.push(cornerWidthRight, cornerConfigRight);

    errorArray.push(errors.widthErrCornerRight);
  }

  if (isCombo) {
    required.push(comboWindowUnitSize, comboWindowHeight, comboWindowConfig);

    errorArray.push(errors.widthErrComboWindow, errors.heightErrComboWindow);
  }

  const showPreview = required.every((itm) => itm);
  const hasErrors = errorArray.some((itm) => itm);

  const extClad =
    material?.includes("Clad") &&
    exteriorCladColor &&
    !exteriorCladColor.includes("Custom")
      ? exteriorCladColor
      : isAluminum &&
          aluminumColorExterior &&
          !aluminumColorExterior.includes("Custom")
        ? aluminumColorExterior
        : null;

  const cladColor = extClad ? cladColors[extClad] : "#888886";

  const strokeStyle =
    extClad?.includes("Black") || extClad?.includes("Brown")
      ? "white"
      : "black";

  const lockSideSidelite = exactConfigImg?.includes("LOCK");

  const sideliteSide =
    (!isSwing && !isPivot) || sideLite === "none"
      ? null
      : sideLite === "Add Double Sidelite"
        ? "both"
        : swingType === "Pair Swing" &&
            sideLite === "Add Single Sidelite" &&
            exactConfigImg?.includes("LEFT_SL")
          ? "left"
          : swingType === "Pair Swing" &&
              sideLite === "Add Single Sidelite" &&
              exactConfigImg?.includes("RIGHT_SL")
            ? "right"
            : exactConfig.includes("LH") && lockSideSidelite
              ? "right"
              : exactConfig.includes("LH") && !lockSideSidelite
                ? "left"
                : exactConfig.includes("RH") && lockSideSidelite
                  ? "left"
                  : exactConfig.includes("RH") && !lockSideSidelite
                    ? "right"
                    : null;

  const numPanels = isBifold
    ? findNumPanelsBifold(exactConfig)
    : isSlider
      ? findNumPanelsSlider(exactConfig)
      : isSwing
        ? findNumPanelsSwing(swingType || "", sideLite || "none")
        : isPivot
          ? findNumPanelsPivot(sideLite || "none")
          : 1;

  const numPanelsRightCorner = isCorner
    ? findNumPanelsBifold(cornerConfigRight || "")
    : 0;

  const numPanelsComboWindow = isCombo
    ? findNumPanelsBifold(comboWindowConfig || "")
    : 0;

  const panelWidth = isCorner
    ? calculateCornerPanelWidth({
        stiles: stiles || "",
        numberOfPanels: numPanels,
        material,
        width: Number(cornerFrameWidthLeft || "0"),
        isPrimary: primaryDoor === "Left",
        exactConfig,
      })
    : calculatePanelWidth({
        doorType,
        netWidth,
        numberOfPanels: numPanels,
        material,
        exactConfig,
        stilesAndRails: stilesAndRails || "",
        screenType: addScreen && screenType ? screenType : "",
        swingPanelWidth,
        swingPanelFraction,
        sliderPocket,
        handleStyle,
        fullboundProfile: fullboundProfile || "",
        isComboDoor: isCombo,
      });

  const panelWidthRightCorner = isCorner
    ? calculateCornerPanelWidth({
        stiles: stiles || "",
        numberOfPanels: numPanelsRightCorner || 0,
        material,
        width: Number(cornerFrameWidthRight || "0"),
        isPrimary: primaryDoor === "Right",
        exactConfig: cornerConfigRight || "",
      })
    : 0;

  const panelWidthWindowCombo = isCombo
    ? calculatePanelWidth({
        doorType,
        netWidth: netWidthComboWindow,
        numberOfPanels: numPanelsComboWindow,
        material,
        exactConfig: comboWindowConfig || "",
        stilesAndRails: stilesAndRails || "",
        screenType: addScreen && screenType ? screenType : "",
        isComboWindow: true,
      })
    : 0;

  const panelHeight = calculatePanelHeight({
    exactConfig,
    doorType,
    sillStyle: sillStyle || "",
    netHeight,
    screenType: addScreen && screenType ? screenType : "",
    material,
    isPico: isPico,
    swingDirection,
    fullboundProfile: fullboundProfile || "",
    alumThickFrame: alumThickFrame || false,
  });

  const panelHeightWindowCombo = isCombo
    ? calculatePanelHeight({
        exactConfig: comboWindowConfig || "",
        doorType,
        sillStyle:
          isComboSillDifferent && comboWindowSillStyle
            ? comboWindowSillStyle
            : sillStyle
              ? sillStyle
              : "",
        netHeight: netHeightComboWindow,
        isPico: isPico,
        screenType: addScreen && screenType ? screenType : "",
        material,
      })
    : 0;

  const glass = isAluminum
    ? calculateDLOAlum(
        doorType,
        exactConfig,
        panelWidth || 0,
        panelHeight,
        fullboundProfile || "",
        false,
        swingDirection || "",
        sillStyle || "",
      )
    : calculateDLO(
        stiles || "",
        stilesAndRails || "",
        lockRail || "",
        panelWidth || 0,
        panelHeight,
        doorType,
        hasBluetoothHandle,
        swingDirection || "",
      );

  const isAlumSidelite =
    isAluminum && sideLite !== "none" && isSwing ? true : false;

  const alumSideliteHeight = isAlumSidelite
    ? calculateAlumSideliteHeight({
        netHeight,
        screenType: addScreen && screenType ? screenType : "",
        swingDirection: swingDirection || "",
        alumThickFrame: alumThickFrame || false,
      })
    : 0;

  const glassAlumSidelite = isAlumSidelite
    ? calculateDLOAlum(
        doorType,
        exactConfig,
        fractionToInt(
          sideLiteWidth?.split(" ")[0] || "",
          sideLiteWidth?.split(" ")[1] || "",
        ),
        alumSideliteHeight,
        fullboundProfile || "",
        true,
        swingDirection || "",
        sillStyle || "",
      )
    : { glassWidth: 0, glassHeight: 0 };

  const glassRightCorner = isCorner
    ? calculateDLO(
        stiles || "",
        stilesAndRails || "",
        lockRail || "",
        panelWidthRightCorner || 0,
        panelHeight,
        doorType,
        hasBluetoothHandle,
        "",
      )
    : { glassHeight: "", glassWidth: "", glassWidthDD: "" };

  const glassWindowCombo = isCombo
    ? calculateDLO(
        stiles || "",
        stilesAndRails || "",
        lockRail || "",
        panelWidthWindowCombo || 0,
        panelHeightWindowCombo,
        doorType,
        false,
        "",
      )
    : { glassHeight: "", glassWidth: "", glassWidthDD: "" };

  const pocketSlider =
    isSlider && sliderPocket === "With Pocket"
      ? pocketSliderCalc(exactConfig.includes("-"), netWidth, panelWidth || 0)
      : { pocketSize: 0, netWidth: 0 };

  const scale = getScale(
    doorType,
    isCorner ? netWidthCornerRight : isCombo ? netWidthComboWindow + 0.9375 : 0,
    netWidth,
    netHeight,
    dataUrl && netHeight >= netWidth ? canvasWidth - 200 : canvasWidth,
    dataUrl && netHeight >= netWidth ? canvasHeight - 200 : canvasHeight,
    sliderPocket || "",
    pocketSlider.netWidth || 0,
  );

  const doorWidthPixels = netWidth * scale;

  const doorHeightPixels = netHeight * scale;

  //add .9375 for exterior view canvas
  const comboWindowWidthPixels = isCombo
    ? (netWidthComboWindow + 0.9375) * scale
    : 0;

  const comboWindowHeightPixels = isCombo ? netHeightComboWindow * scale : 0;

  const alumSideliteHeightPixels = isAlumSidelite
    ? calcAlumSideliteHeightPixels(
        scale,
        netHeight,
        swingDirection || "",
        alumThickFrame || false,
      )
    : 0;

  const frame = isAluminum
    ? frameDimensionsAlum(
        isFixedPanel && exactConfig === "Direct Glaze"
          ? "Direct Glaze"
          : isFixedPanel
            ? `Aluminum Fixed Full Bound ${
                exactConfig?.includes("Inswing") ? "Inswing" : "Outswing"
              } ${fullboundProfile?.includes("Window") ? "Window" : "Door"}`
            : isFixedPanel
              ? exactConfig
              : isSwing
                ? `Aluminum Swing`
                : doorType?.includes("Window") && !isBifold
                  ? "Aluminum Fixed Full Bound Outswing Window"
                  : doorType,
        scale,
        alumThickFrame || false,
        swingDirection || "",
        sillStyle || "",
        exactConfig || "",
        addScreen && screenType?.includes("Centor") ? true : false,
      )
    : frameDimensions(
        isFixedPanel ? exactConfig : doorType,
        scale,
        swingDirection || "",
        isSlider && sliderPocket === "With Pocket" ? true : false,
        addScreen && screenType?.includes("Centor") ? true : false,
        exactConfig?.includes("-") || false,
        false,
        primaryDoor || "",
        cornerType || "",
        stiles,
        exactConfig || "",
        exactConfigImg || "",
      );

  const frameRightCorner = isCorner
    ? frameDimensions(
        doorType,
        scale,
        "",
        false,
        addScreen && screenType?.includes("Centor") ? true : false,
        false,
        true,
        primaryDoor || "",
        cornerType || "",
        stiles,
      )
    : { jambLeg: 0, headJamb: 0, sill: 0 };

  const frameWindowCombo = isCombo
    ? frameDimensions(
        doorType,
        scale,
        "",
        false,
        addScreen && screenType?.includes("Centor") ? true : false,
        false,
        true,
        primaryDoor || "",
        "",
        stiles,
      )
    : { jambLeg: 0, headJamb: 0, sill: 0 };

  const gaps = isAluminum
    ? getGapsAlum(
        scale,
        doorType,
        exactConfig,
        exactConfigImg || "",
        alumThickFrame || false,
      )
    : getGaps(scale, doorType, exactConfig, exactConfigImg || "", isPico);

  const pocketSize = pocketSlider.pocketSize * scale;

  const furthestXPos = isCorner
    ? 120 + doorWidthPixels + (150 + (netWidthCornerRight || 0) * scale)
    : isCombo
      ? 120 + doorWidthPixels + comboWindowWidthPixels
      : isSlider && sliderPocket === "With Pocket"
        ? pocketSlider.netWidth * scale + 120
        : 120 + doorWidthPixels;

  const distanceToMiddle =
    dataUrl && netHeight >= netWidth
      ? (canvasWidth - 200 - furthestXPos) / 2
      : (canvasWidth - furthestXPos) / 2;

  const woodImg = woodImgs.find((itm) => itm.label === woodSpecies) || {
    imgLabel: "",
  };

  const font =
    dataUrl && netWidth > netHeight ? `bold 15px Arial` : "bold 12px Arial";

  const handleHeightPixels =
    isBifoldWindow && isPico
      ? 19 * scale
      : isBifoldWindow
        ? 12 * scale
        : handleHeight === '42"'
          ? 42 * scale
          : handleHeight === '31"'
            ? 31 * scale
            : 36 * scale;

  interface DrawDoor {
    frameXStart: number;
    frameYEnd: number | null;
  }

  const drawDoor = (canvasArg: {
    frameWidthPixels: number;
    frameHeightPixels: number;
    frameWidthStr: string;
    frameHeightStr: string;
    panelWidthInt: number;
    panelHeightInt: number;
    glassObj: typeof glass;
    configChoice: string;
    numberOfPanels: number;
    stileCategory: string;
    isRightCorner: boolean;
    comboRightUnit: string;
    isComboWindow?: boolean;
    canvasElem: React.RefObject<HTMLCanvasElement>;
    firstUnitFrameXStart?: number | null;
    firstUnitFrameYEnd?: number | null;
    finalUnit?: boolean | null;
  }): DrawDoor | null => {
    const {
      frameWidthPixels,
      frameHeightPixels,
      frameWidthStr,
      frameHeightStr,
      panelWidthInt,
      panelHeightInt,
      glassObj,
      configChoice,
      numberOfPanels,
      stileCategory,
      isRightCorner,
      comboRightUnit,
      isComboWindow,
      canvasElem,
      firstUnitFrameXStart,
      firstUnitFrameYEnd,
      finalUnit,
    } = canvasArg;

    const panelWidthPixels = panelWidthInt * scale;
    const panelHeightPixels = panelHeightInt * scale;

    const drawId = drawIdRef.current;
    const isStaleDraw = () => drawId !== drawIdRef.current;

    const canvas = canvasElem?.current;
    if (!canvas) {
      return null;
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    if (isInitialRender && containerWindow) {
      clearCanvas(canvasElem); // Clear the canvas before drawing to avoid overlapping drawings
      setIsInitialRender(false);
    }

    const hasSliderPocket =
      isSlider && configChoice.includes("P") ? true : false;

    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = 0.5;
    ctx.font = font;

    const lineHeight = ctx.measureText("M").width;

    const panelWidthPicoShootboltPixels = isFixedPanel
      ? panelWidthPixels
      : panelWidthPixels + PICO_ADD * scale;

    const sideliteWidthPixels = isAlumSidelite
      ? calcSideliteWidthAlumPixels(
          scale,
          netWidth,
          swingDirection || "",
          swingType || "",
          sideLite || "",
          panelWidthPixels / scale,
          equalPanels || false,
          alumThickFrame || false,
        )
      : (isSwing || isPivot) && exactConfig.includes("Sidelite")
        ? fractionToInt(
            sideLiteWidth?.split(" ")[0] || "",
            sideLiteWidth?.split(" ")[1] || "",
          ) * scale
        : 0;

    const rightCornerDistance = 200;

    const xStart = isRightCorner
      ? rightCornerDistance + doorWidthPixels + distanceToMiddle
      : comboRightUnit
        ? 50 +
          (comboRightUnit === "Window"
            ? doorWidthPixels - frame.jambLeg
            : comboWindowWidthPixels - frame.jambLeg) +
          distanceToMiddle
        : 50 + distanceToMiddle;
    const yStart = dataUrl ? 70 : 50;

    const alumFixedThickness = !isAluminum
      ? 0
      : isFixedPanel && exactConfig === "Direct Glaze"
        ? 1.5 * scale
        : isFixedPanel &&
            exactConfig?.includes("Inswing") &&
            fullboundProfile?.includes("Window")
          ? 1.65338 * scale
          : isFixedPanel &&
              exactConfig?.includes("Inswing") &&
              fullboundProfile?.includes("Door")
            ? 2.499996 * scale
            : isFixedPanel &&
                exactConfig?.includes("Outswing") &&
                fullboundProfile?.includes("Window")
              ? 2.448654 * scale
              : isFixedPanel &&
                  exactConfig?.includes("Outswing") &&
                  fullboundProfile?.includes("Door")
                ? 3.310707 * scale
                : doorType?.includes("Window")
                  ? 3.310707 * scale
                  : isSwing && swingDirection === "Inswing"
                    ? 2.499996 * scale
                    : isSwing && swingDirection === "Outswing"
                      ? 3.248025 * scale
                      : isBifoldDoor || isBifoldWindow
                        ? 1.9291 * scale
                        : 0;

    // const alumSideliteThickness = 2.09375 * scale;
    const alumSideliteThickness =
      doorType === "Inswing" ? 1.32 * scale : 1.92913 * scale;

    const stileThickness =
      isPico && !isSwing
        ? 1.875 * scale
        : isPico && isSwing
          ? 2.5 * scale
          : stileCategory?.includes("Slimline")
            ? 2.5 * scale
            : stileCategory === "Euro"
              ? 3.4375 * scale
              : 4.75 * scale;

    const slim = 2.5 * scale;

    const slimLockStile = 3.4375 * scale;

    const tradStile = 4.75 * scale;

    const frameXStart =
      isRightCorner && cornerType === "Inside"
        ? xStart
        : isRightCorner
          ? xStart - frameRightCorner.jambLeg
          : comboRightUnit === "Window"
            ? xStart - frameWindowCombo.jambLeg
            : xStart - frame.jambLeg;
    const frameYStart = yStart - frame.headJamb;

    const frameWidth =
      cornerType === "Inside" && !isRightCorner
        ? (cornerFrameWidthLeft || 0) * scale
        : cornerType === "Inside" && isRightCorner
          ? (cornerFrameWidthRight || 0) * scale
          : frameWidthPixels;
    const frameHeight = frameHeightPixels;

    let panelWidthX = 0;

    if (!isRightCorner && !comboRightUnit) {
      ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear canvas
    } else {
      ctx.clearRect(frameXStart, frameYStart, frameWidthPixels, canvas.height);
    }

    function canvasDraw(
      fillStyle: string | CanvasPattern,
      railsWoodImg?: CanvasPattern,
    ) {
      if (!ctx) return;
      ctx.fillStyle = fillStyle; // Set fill color for the stiles and rails

      let frameArgs: FillRectArgs;

      if (hasSliderPocket) {
        frameArgs = [
          frameXStart,
          frameYStart,
          pocketSlider.netWidth * scale,
          frameHeight,
        ];
      } else {
        frameArgs = [frameXStart, frameYStart, frameWidth, frameHeight];
      }
      ctx.fillRect(...frameArgs); //fills the box
      ctx.strokeRect(...frameArgs);

      const handleObj = isBifold
        ? findHandlePosition(configChoice, numberOfPanels)
        : isSwing && isAluminum
          ? findHandlePositionSwingAlum(configChoice, exactConfigImg || "")
          : isSwing
            ? findHandlePositionSwing(
                exactConfig,
                exactConfigImg || "",
                isPico,
                secondaryHandle || "",
              )
            : isPivot
              ? findHandlePositionSwing(
                  exactConfig.replace("Pivot", "Single"),
                  exactConfigImg || "",
                  isPico,
                  secondaryHandle || "",
                )
              : isSlider
                ? findHandlePositionSlider(
                    exactConfig,
                    sliderPocket === "With Pocket",
                  )
                : null;

      function drawHandle(
        handleSide: string,
        i: number,
        panelX: number,
        pixelWidth: number,
      ) {
        if (!ctx) return;
        if (!handleColor || canvasArg.isComboWindow) return;

        const twoHandles =
          typeof handleObj === "object" && handleObj?.numHandles === 2
            ? true
            : false;

        const thinHandle =
          handleStyle?.includes("Truth") ||
          (isSlider && handleStyle === "Dallas")
            ? true
            : false;

        const backset = 1.375 * scale;

        let backplateWidth =
          isSlider && thinHandle
            ? slimLockStile / 2
            : isSlider
              ? slimLockStile
              : isPivot
                ? slimLockStile * 2.4
                : slimLockStile * 2;
        let backplateHeight = isSlider
          ? panelHeightPixels / 7
          : isPivot
            ? panelHeightPixels / 25
            : panelHeightPixels / 8;
        let backplateX = 0;

        if (handleSide === "Left") {
          if (isSlider) {
            backplateX = xStart + panelX + backset / 2;
          } else {
            if (isPivot) {
              backplateX = xStart + panelX + slimLockStile / 12;
            } else {
              backplateX = xStart + panelX + (stileLeft - backset) / 2;
            }
          }
        } else {
          if (isSlider) {
            if (thinHandle) {
              backplateX =
                xStart +
                panelX +
                panelWidthPixels -
                slimLockStile +
                slimLockStile -
                backplateWidth -
                backset / 2;
            } else {
              backplateX =
                xStart +
                panelX +
                panelWidthPixels -
                slimLockStile -
                backset / 2;
            }
          } else {
            const sub = isPivot ? 0 : (stileRight - backset) / 2;
            backplateX =
              xStart +
              panelX +
              pixelWidth -
              slimLockStile -
              backplateWidth +
              slimLockStile -
              sub;
          }
        }

        if (isRightCorner) {
          backplateX =
            handleSide === "Left"
              ? rightCornerDistance +
                distanceToMiddle +
                doorWidthPixels +
                backset / 2
              : rightCornerDistance +
                doorWidthPixels +
                40 +
                pixelWidth +
                i * pixelWidth;
        } else if (comboRightUnit) {
          const frameWidthToUse =
            comboRightUnit === "Window"
              ? doorWidthPixels
              : comboWindowWidthPixels;
          backplateX =
            handleSide === "Left"
              ? xStart + backset / 2
              : frameWidthToUse + 40 + pixelWidth + i * pixelWidth;
        }

        let backplateY = yStart + panelHeightPixels - handleHeightPixels; // Center backplate vertically

        let handleHeightOffset = 5;
        let handleX =
          handleSide === "Left" ? backplateX : backplateX + backplateWidth;
        let handleY = backplateY + backplateHeight / 2 - handleHeightOffset / 2; // Center handle vertically on backplate

        if (hasHandle && handleStyle) {
          ctx.save();
          const canvasImg = new Image();
          canvasImg.crossOrigin = "anonymous"; // Enable cross-origin
          canvasImg.src = handleImage(
            doorType,
            handleStyle,
            handleSide,
            handleColor || "",
          );

          canvasImg.onload = () => {
            if (isStaleDraw()) return;
            ctx.drawImage(
              canvasImg,
              backplateX,
              isPivot
                ? backplateY - backplateHeight - backplateHeight / 2
                : thinHandle
                  ? backplateY - backplateHeight / 3
                  : backplateY,
              backplateWidth,
              handleStyle?.includes("Touch")
                ? backplateHeight - 8 * scale
                : backplateHeight,
            );
          };
          ctx.restore();

          if (isBifold && handleColor) {
            ctx.save();
            const backplateImg = new Image();
            backplateImg.crossOrigin = "anonymous"; // Enable cross-origin
            backplateImg.src = alumBifoldCylinderImage(handleColor || "");
            backplateImg.onload = () => {
              if (isStaleDraw()) return;
              ctx.drawImage(
                backplateImg,
                handleSide === "Left"
                  ? backplateX
                  : backplateX + slimLockStile * 1.5,
                backplateY + 5 * scale,
                backplateWidth / 4,
                backplateWidth / 3,
              );
            };
            ctx.restore();
          }

          if (isPivot && pivotBackPlate && pivotBackPlateColor) {
            ctx.save();
            const backplateImg = new Image();
            backplateImg.crossOrigin = "anonymous"; // Enable cross-origin
            backplateImg.src = pivotBackPlateImage(
              pivotBackPlate || "",
              pivotBackPlateColor || "",
            );
            backplateImg.onload = () => {
              if (isStaleDraw()) return;
              ctx.drawImage(
                backplateImg,
                handleSide === "Left"
                  ? backplateX
                  : backplateX + slimLockStile * 1.5,
                backplateY,
                backplateWidth / 3,
                backplateWidth / 3,
              );
            };
            ctx.restore();
          }

          //returning because the handle height of the second handle does not need to be displayed
          if (
            twoHandles &&
            handleSide === "Left" &&
            (!isPico ||
              (isPico &&
                isSwing &&
                secondaryHandle &&
                secondaryHandle === "Matching Dummy Handle"))
          )
            return;
          ctx.strokeStyle = "black";

          // handle height dimension line
          ctx.strokeStyle = "black";
          // ctx.beginPath();
          const handleDimX =
            handleSide === "Left" && isSlider
              ? handleX + backplateWidth * (thinHandle ? 3 : 2)
              : handleSide === "Left"
                ? handleX + backplateWidth
                : isSlider
                  ? handleX - backplateWidth * (thinHandle ? 3 : 2)
                  : handleX - backplateWidth * 1.1;
          drawLine(
            ctx,
            handleDimX,
            handleY,
            handleDimX,
            yStart + panelHeightPixels,
          );

          ctx.save(); // Save current state
          ctx.fillStyle = "black";
          ctx.font = font;

          ctx.translate(
            handleSide === "Left"
              ? handleDimX + 5 * scale
              : handleDimX - 2 * scale,
            (yStart + frameHeightPixels + handleY) / 2,
          );
          ctx.rotate(-Math.PI / 2); // Rotate counter-clockwise
          const handleHeigthText =
            isBifoldWindow && isPico
              ? '19"'
              : isBifoldWindow
                ? '16"'
                : handleHeight === '42"'
                  ? '42"'
                  : handleHeight === '31"'
                    ? '31"'
                    : '36"';
          ctx.fillText(handleHeigthText, 0, 0); // Draw text
          ctx.restore();
        } else if (isPivot && pivotBackPlate && pivotBackPlateColor) {
          ctx.save();
          const backplateImg = new Image();
          backplateImg.crossOrigin = "anonymous"; // Enable cross-origin
          backplateImg.src = pivotBackPlateImage(
            pivotBackPlate || "",
            pivotBackPlateColor || "",
          );
          backplateImg.onload = () => {
            if (isStaleDraw()) return;
            ctx.drawImage(
              backplateImg,
              handleSide === "Left"
                ? backplateX
                : backplateX + slimLockStile * 1.5,
              backplateY,
              backplateWidth / 3,
              backplateWidth / 3,
            );
          };
          ctx.restore();

          // handle height dimension line
          const handleDimX =
            handleSide === "Left"
              ? handleX + backplateWidth
              : handleX - backplateWidth * 1.5;
          drawLine(
            ctx,
            handleDimX,
            handleY,
            handleDimX,
            yStart + panelHeightPixels,
          );

          ctx.save(); // Save current state
          ctx.fillStyle = "black";
          ctx.font = font;

          ctx.translate(
            handleDimX + 15,
            (yStart + frameHeightPixels + handleY) / 2,
          );
          ctx.rotate(-Math.PI / 2); // Rotate counter-clockwise
          const handleHeigthText = handleHeight === '42"' ? '42"' : '36"';
          ctx.fillText(handleHeigthText, 0, 0); // Draw text
          ctx.restore();
        }
      }

      function drawShootbolt(i: number, panelX: number, pixelWidth: number) {
        if (!ctx) return;

        const twoHandles =
          typeof handleObj === "object" && handleObj?.numHandles === 2
            ? true
            : false;

        let handlePanels: number[] = [];

        if (
          typeof handleObj === "object" &&
          (!doorType.includes("Window") || isCombo)
        ) {
          if (twoHandles) {
            if (
              handleObj &&
              "panelWithHandleLeft" in handleObj &&
              handleObj?.panelWithHandleLeft
            ) {
              handlePanels.push(handleObj.panelWithHandleLeft);
            }

            if (
              handleObj &&
              "panelWithHandleRight" in handleObj &&
              handleObj?.panelWithHandleRight
            ) {
              handlePanels.push(handleObj.panelWithHandleRight);
            }
          } else if (!twoHandles) {
            if (handleObj?.panelWithHandle) {
              handlePanels.push(handleObj.panelWithHandle);
            }
          }
        } else if (isPico && doorType.includes("Window")) {
          const boltObj = findOddOddShootboltPositionPicoWindow(configChoice);
          if (boltObj?.numHandles) {
            handlePanels.push(
              primaryDoor === "Left"
                ? boltObj.panelWithBoltRight
                : boltObj.panelWithBoltLeft,
            );
          }
        }

        const currentPanel = i + 1;

        if (handlePanels.includes(currentPanel)) {
          return;
        }

        let backplateWidth = slim / 2;
        let backplateHeight = backplateWidth * 6;
        let backplateX = 0;
        let panelRight = 0;

        let numLeft = 0;
        let numRight = 0;

        if (isRightCorner) {
          numLeft = sumDigitsFromString(exactConfig);
          numRight = sumDigitsFromString(configChoice);

          panelRight = numRight - i;

          const isOddPanel = panelRight % 2 !== 0;

          if (panelRight && isOddPanel) {
            backplateX =
              rightCornerDistance +
              doorWidthPixels +
              distanceToMiddle +
              panelX +
              stileThickness / 3;
          } else {
            return;
          }
        } else if (comboRightUnit) {
          numLeft = sumDigitsFromString(
            comboRightUnit === "Window"
              ? exactConfig
              : comboWindowConfig
                ? comboWindowConfig
                : "",
          );
          numRight = sumDigitsFromString(configChoice);

          panelRight = numRight - i;

          const isOddPanel = panelRight % 2 !== 0;

          if (panelRight && isOddPanel) {
            backplateX = xStart + panelX + stileThickness / 3;
          } else {
            return;
          }
        } else {
          numLeft = configChoice.includes("L")
            ? Number(configChoice.split("L")[0])
            : 0;
          numRight =
            configChoice.includes("R") && !numLeft
              ? Number(configChoice.split("R")[0])
              : numLeft && configChoice.includes("R")
                ? Number(configChoice.split("L")[1].split("R")[0])
                : 0;

          panelRight =
            currentPanel > numLeft
              ? numRight - (currentPanel - numLeft - 1)
              : 0;

          const isOddPanel =
            currentPanel > numLeft
              ? panelRight % 2 !== 0
              : currentPanel % 2 !== 0;

          if (numLeft && numLeft >= currentPanel && isOddPanel) {
            backplateX = xStart + panelX + pixelWidth - stileRight / 1.3;
          } else if (panelRight && isOddPanel) {
            backplateX = xStart + panelX + (stileLeft - backplateWidth) / 2;
          } else {
            return;
          }
        }

        let backplateY = yStart + panelHeightPixels - handleHeightPixels; // Center backplate vertically

        if (canvasArg.isComboWindow) {
          const handleHeightWindow = isPico ? 19 * scale : 12 * scale;
          backplateY = yStart + panelHeightPixels - handleHeightWindow;
        }

        ctx.save();
        const canvasImg = new Image();
        canvasImg.crossOrigin = "anonymous"; // Enable cross-origin
        let sbColor = "";

        const imgUrl =
          "https://res.cloudinary.com/ag-millworks/image/upload/Canvas/handles/Bifold_Swings";

        let shootBoltExt = false;

        if (showExtShootbolts) {
          sbColor = shootBoltColor || "Stainless Steel";
          const evenConfig = noAccess.includes(configChoice);
          if (
            isRightCorner &&
            evenConfig &&
            numRight &&
            numRight % 2 === 0 &&
            currentPanel === 2
          ) {
            if (primaryDoor === "Right") {
              canvasImg.src = picoShootbolt(true, sbColor);
              shootBoltExt = true;
            } else {
              canvasImg.src = picoShootbolt(false, sbColor);
            }
          } else if (
            evenConfig &&
            numLeft &&
            numLeft % 2 === 0 &&
            currentPanel === numLeft - 1
          ) {
            if (configChoice.includes("L") && configChoice.includes("R")) {
              if (primaryDoor === "Left") {
                canvasImg.src = picoShootbolt(true, sbColor);
                shootBoltExt = true;
              } else {
                canvasImg.src = picoShootbolt(false, sbColor);
                shootBoltExt = false;
              }
            } else {
              if ((isCorner && primaryDoor === "Left") || !isCorner) {
                canvasImg.src = picoShootbolt(true, sbColor);
                shootBoltExt = true;
              } else {
                canvasImg.src = picoShootbolt(false, sbColor);
              }
            }
          } else if (
            evenConfig &&
            numRight &&
            numRight % 2 === 0 &&
            currentPanel === numLeft + 2
          ) {
            if (configChoice.includes("L") && configChoice.includes("R")) {
              if (primaryDoor === "Right") {
                canvasImg.src = picoShootbolt(true, sbColor);
                shootBoltExt = true;
              } else {
                canvasImg.src = picoShootbolt(false, sbColor);
                shootBoltExt = false;
              }
            } else {
              canvasImg.src = picoShootbolt(true, sbColor);
              shootBoltExt = true;
            }
          } else {
            canvasImg.src = picoShootbolt(false, sbColor);
            shootBoltExt = false;
          }
        } else {
          sbColor = shootBoltColor || "Brushed Chrome";
          canvasImg.src = `${imgUrl}/Shootbolt_${sbColor?.replace(
            " ",
            "_",
          )}.svg`;
          shootBoltExt = false;
        }

        canvasImg.onload = () => {
          if (isStaleDraw()) return;
          ctx.drawImage(
            canvasImg,
            backplateX,
            backplateY,
            backplateWidth,
            shootBoltExt ? backplateHeight + backplateWidth : backplateHeight,
          );
        };
        ctx.restore();

        ctx.save();
        ctx.strokeStyle = "black";
        ctx.fillStyle = "black";
        const fontNum = 3;
        ctx.font = `${fontNum * scale > 10 ? 10 : fontNum * scale}px Arial`;

        if (panelRight || isRightCorner) {
          drawLine(
            ctx,
            backplateX,
            backplateY - 5,
            backplateX,
            backplateY - 30,
          );

          const xLength = 5 * scale > 20 ? 20 : 5 * scale;

          drawLine(
            ctx,
            backplateX,
            backplateY - 30,
            backplateX + xLength,
            backplateY - 30,
          );

          // const xInitial = 2 * scale;
          const xInitial = pixelWidth / 4;

          ctx.fillText("SHOOTBOLT", backplateX + xInitial, backplateY - 30);
          ctx.fillText("FROM INT.", backplateX + xInitial, backplateY - 20);
          if (shootBoltExt) {
            ctx.fillText("& EXT.", backplateX + xInitial, backplateY - 10);
          }
          ctx.restore();
        } else {
          drawLine(
            ctx,
            backplateX,
            backplateY - 5,
            backplateX,
            backplateY - 30,
          );

          const xLength = 5 * scale > 85 ? 85 : 5 * scale;

          drawLine(
            ctx,
            backplateX,
            backplateY - 30,
            backplateX - xLength,
            backplateY - 30,
          );

          const xInitial = pixelWidth / (shootBoltExt ? 1.3 : 1.5);

          ctx.fillText("SHOOTBOLT", backplateX - xInitial, backplateY - 30);
          ctx.fillText("FROM INT.", backplateX - xInitial, backplateY - 20);
          if (shootBoltExt) {
            ctx.fillText("& EXT.", backplateX - xInitial, backplateY - 10);
          }
          ctx.restore();
        }
      }

      function drawDloDD(innerStileWidth: number, i: number) {
        if (!ctx) return;
        if (!isBifold || !stileCategory.includes("Slimline")) return;

        if (doorType.includes("Window")) {
          return;
        }
        // Draw dlo dd glass width
        ctx.save();
        ctx.strokeStyle = "black";
        ctx.fillStyle = "black";
        ctx.beginPath();
        const pixelWidth = isPico
          ? panelWidthPicoShootboltPixels
          : panelWidthPixels;
        const start = xStart + i * pixelWidth + stileLeft;
        const end = start + innerStileWidth;
        drawLine(ctx, start, frameYStart - 10, end, frameYStart - 10);

        const glassText =
          "glassWidthDD" in glassObj && glassObj.glassWidthDD
            ? `${glassObj.glassWidthDD}"`
            : `${glassObj.glassWidth}"`;
        drawWidthText(
          ctx,
          ["DLO", "DLO"],
          glassText,
          innerStileWidth,
          start,
          frameYStart - 15 - lineHeight,
          frameYStart - 15,
        );

        ctx.restore();
      }

      function drawSliderPocket(x: number, y: number) {
        if (!ctx) return;
        ctx.save();
        ctx.strokeStyle = "black";
        ctx.beginPath(); // Start a new path for the "X"
        ctx.moveTo(x, y); // Move to the top-left corner of the panel
        const xPos = x + pocketSize;
        const yPos = y + panelHeightPixels;
        ctx.lineTo(xPos, yPos); // Draw line to the bottom-right corner
        ctx.moveTo(xPos, y); // Move to the top-right corner of the panel
        ctx.lineTo(x, yPos); // Draw line to the bottom-left corner
        ctx.stroke(); // Stroke the path, which draws the "X"
        ctx.restore();
      }

      function drawHashMarks(isSidelite: boolean) {
        if (!ctx) return;
        ctx.save();
        ctx.strokeStyle = "black";
        const adder = isSidelite ? sideliteWidthPixels : panelWidthPixels;
        const x = xStart + panelWidthX + adder / 2 - 3 * scale;
        const y = yStart + panelHeightPixels / 3;

        ctx.moveTo(x, y);
        ctx.lineTo(x + 3 * scale, y + 3 * scale);

        const x2 = x - 1 * scale;
        ctx.moveTo(x2, y - 2 * scale);
        ctx.lineTo(x2 + 7 * scale, y + 5 * scale);

        const x3 = x + 2 * scale;
        ctx.moveTo(x3, y);
        ctx.lineTo(x3 + 3 * scale, y + 3 * scale);

        ctx.stroke();
        ctx.restore();
      }

      let stileLeft: number = 0;
      let stileRight: number = 0;
      let railTop = isPico
        ? 1.875 * scale
        : stileCategory?.includes("Slimline")
          ? 2.5 * scale
          : stileCategory === "Euro"
            ? 3.4375 * scale
            : 4.75 * scale;
      let railBtm = isPico
        ? 1.875 * scale
        : stileCategory?.includes("Slimline") && isSlider
          ? 3.4375 * scale
          : stileCategory?.includes("Slimline")
            ? 2.5 * scale
            : stileCategory === "Euro"
              ? 3.4375 * scale
              : stilesAndRails === 'Bottom rail 7"'
                ? 7 * scale
                : 4.75 * scale;

      let dloPanel =
        handleObj &&
        handleObj.numHandles === 2 &&
        "panelWithHandleLeft" in handleObj &&
        "panelWithHandleRight" in handleObj
          ? [...Array(numberOfPanels).keys()].find(
              (num) =>
                num + 1 !== handleObj.panelWithHandleLeft &&
                num + 1 !== handleObj.panelWithHandleRight,
            )
          : handleObj && handleObj.numHandles === 1
            ? [...Array(numberOfPanels).keys()].find(
                (num) => num + 1 !== handleObj.panelWithHandle,
              )
            : 0;

      const isFritsjurgen =
        isPivot && pivotHardware === "Fritsjurgens" ? true : false;

      if (isFritsjurgen) {
        if (pivotAction === "Free Swing") {
          railTop = stileCategory?.includes("Slimline")
            ? 3.4375 * scale
            : stileCategory === "Euro"
              ? 3.4375 * scale
              : 4.75 * scale;
        } else {
          railTop = stileCategory === "Euro" ? 3.4375 * scale : 4.75 * scale;
          railBtm =
            stilesAndRails === 'Bottom rail 7"' ? 7 * scale : 4.75 * scale;
        }
      }

      const XSlider = configChoice.split("").every((letter) => letter === "X");

      const picoSboltPanels =
        isPico && isBifold ? picoShootboltPanels(configChoice) : [];

      for (let i = 0; i < numberOfPanels; i++) {
        const isPicoSboltPanel = picoSboltPanels.includes(i + 1);
        const pixelWidthPanel = isPicoSboltPanel
          ? panelWidthPicoShootboltPixels
          : panelWidthPixels;
        // Draw and fill the outer rectangle (stile) with gray
        ctx.fillStyle = fillStyle; // Set fill color for the stiles and rails
        ctx.strokeStyle = strokeStyle;

        let panelArgs: FillRectArgs;

        const withPocket =
          isSlider && configChoice.replace("-", "")[i] === "P" ? true : false;

        const isSidelite =
          (isSwing || isPivot) &&
          sideLite !== "none" &&
          ((i === 0 && sideliteSide === "left") ||
            (i + 1 === numberOfPanels && sideliteSide === "right") ||
            ((i === 0 || i + 1 === numberOfPanels) && sideliteSide === "both"))
            ? true
            : false;

        if (isSidelite && isAluminum) {
          railTop = alumSideliteThickness;
          railBtm = alumSideliteThickness;
        } else if (isAluminum) {
          railTop = alumFixedThickness;
          railBtm =
            doorType === "Inswing" && sillStyle?.includes("Flat")
              ? 3.248025 * scale
              : doorType === "Inswing"
                ? 2.773178 * scale
                : alumFixedThickness;
        } else {
          railTop = isPico
            ? 1.875 * scale
            : stileCategory?.includes("Slimline")
              ? 2.5 * scale
              : stileCategory === "Euro"
                ? 3.4375 * scale
                : 4.75 * scale;
          railBtm = isPico
            ? 1.875 * scale
            : stileCategory?.includes("Slimline") && isSlider
              ? 3.4375 * scale
              : stileCategory?.includes("Slimline")
                ? 2.5 * scale
                : stileCategory === "Euro"
                  ? 3.4375 * scale
                  : stilesAndRails === 'Bottom rail 7"'
                    ? 7 * scale
                    : 4.75 * scale;
        }

        const gap = gaps[`panel${i + 1}`];

        if (isSlider) {
          const dashIndex = configChoice.indexOf("-");
          const panelBeforeDash = dashIndex - 1 === i;
          const panelAfterDash = dashIndex === i;

          const overlappedPanelWidth = panelWidthPixels - stileThickness;

          if (
            hasSliderPocket &&
            configChoice[0] === "P" &&
            configChoice.split("").findIndex((itm) => itm === "P") + 1 === i
          ) {
            panelWidthX += gap;
            panelWidthX += pocketSize;
          } else if (i !== 0 && panelAfterDash) {
            panelWidthX += gap;
            panelWidthX += panelWidthPixels;
          } else if (i !== 0) {
            panelWidthX += gap;
            panelWidthX += overlappedPanelWidth;
          }

          const ovWidth =
            (hasSliderPocket &&
              i + 1 === numberOfPanels - 1 &&
              (configChoice.includes("-") ||
                (!configChoice.includes("-") && configChoice[0] !== "P"))) ||
            (hasSliderPocket &&
              i + 1 === numberOfPanels &&
              !configChoice.includes("-") &&
              configChoice[0] === "P") ||
            (!hasSliderPocket && i + 1 === numberOfPanels) ||
            panelBeforeDash
              ? panelWidthPixels
              : overlappedPanelWidth;
          panelArgs = [
            xStart + panelWidthX,
            yStart,
            ovWidth,
            panelHeightPixels,
          ];
        } else {
          if (
            (isSwing || isPivot) &&
            sideLite !== "none" &&
            (sideliteSide === "left" || sideliteSide === "both") &&
            i === 1
          ) {
            panelWidthX += gap;
            panelWidthX += sideliteWidthPixels;

            if (isAlumSidelite) {
              //draw alum frame line around panel
              let xPos = xStart + panelWidthX - gap / 2;
              drawLine(
                ctx,
                xPos,
                frameYStart,
                xPos,
                frameYStart + frameHeightPixels,
              );
            }

            panelArgs = [
              xStart + panelWidthX,
              yStart,
              panelWidthPixels,
              panelHeightPixels,
            ];
          } else if (isSidelite) {
            panelWidthX += gap;

            if (i !== 0) panelWidthX += panelWidthPixels;

            if (isAlumSidelite && i !== 0) {
              //draw alum frame line around panel
              let xPos = xStart + panelWidthX - gap / 2;
              drawLine(
                ctx,
                xPos,
                frameYStart,
                xPos,
                frameYStart + frameHeightPixels,
              );
            }

            const heightToUse = alumSideliteHeightPixels || panelHeightPixels;

            panelArgs = [
              xStart + panelWidthX,
              yStart,
              sideliteWidthPixels,
              heightToUse,
            ];
          } else {
            const isPicoSboltPanelPrev = picoSboltPanels.includes(i);
            const pixelWidthPanelPrev = isPicoSboltPanelPrev
              ? panelWidthPicoShootboltPixels
              : panelWidthPixels;

            panelWidthX += comboRightUnit === "Window" && i === 0 ? 0 : gap;
            if (i !== 0) panelWidthX += pixelWidthPanelPrev;

            panelArgs = [
              xStart + panelWidthX,
              yStart,
              pixelWidthPanel,
              panelHeightPixels,
            ];
          }
        }

        if (withPocket) {
          ctx.save();
          ctx.strokeStyle = "black";
          ctx.fillStyle = "white";
          const pocketSide = i === 0 ? "left" : "right";
          const x =
            pocketSide === "left" ? panelWidthX : panelWidthX + stileThickness;
          ctx.fillRect(xStart + x, frameYStart, pocketSize, frameHeightPixels);
          ctx.strokeRect(
            xStart + x,
            frameYStart,
            pocketSize,
            frameHeightPixels,
          );
          ctx.restore();
          drawSliderPocket(xStart + x, frameYStart);
        } else {
          if (isRightCorner && i === 0 && cornerType === "Outside") {
            ctx.save();
            const xPos = frameXStart;
            ctx.fillStyle = "white";
            ctx.fillRect(
              xPos + frameRightCorner.jambLeg,
              yStart,
              -(frameRightCorner.jambLeg + 2),
              panelHeightPixels,
            );
            ctx.restore();
          }

          if (isAluminum && (isBifoldDoor || isBifoldWindow)) {
            ctx.save();
            ctx.strokeStyle = "black";
            ctx.fillStyle = "black";
            const gasket = 0.4695 * scale;
            ctx.fillRect(
              panelArgs[0] - gasket,
              panelArgs[1] - gasket,
              panelArgs[2] + gasket * 2,
              panelArgs[3] + gasket * 2,
            );
            ctx.restore();
          }

          ctx.fillRect(...panelArgs); // Fill outer rectangle
          ctx.strokeRect(...panelArgs); // Draw outer rectangle border

          if (
            isCorner &&
            !isRightCorner &&
            i === numPanels - 1 &&
            cornerType === "Outside"
          ) {
            ctx.save();
            ctx.fillStyle = "white";
            ctx.strokeStyle = "white";
            ctx.fillRect(
              xStart + panelWidthX + pixelWidthPanel,
              yStart,
              frameXStart +
                frameWidthPixels -
                (xStart + panelWidthX + pixelWidthPanel) +
                2,
              panelHeightPixels,
            );
            ctx.restore();
          }
        }

        let innerStileWidth = pixelWidthPanel - stileThickness - stileThickness;
        let innerStileHeight = panelHeightPixels - railTop - railBtm;

        if (isSlider) {
          innerStileWidth = panelWidthPixels - stileThickness;
        } else if (isSidelite) {
          innerStileWidth = sideliteWidthPixels - slim - slim;
          if (isAlumSidelite) {
            innerStileHeight = alumSideliteHeightPixels - railTop - railBtm;
          }
        }

        const singleHandle =
          handleObj &&
          handleObj.numHandles === 1 &&
          i + 1 === handleObj.panelWithHandle
            ? true
            : false;

        const doubleHandle =
          handleObj && handleObj.numHandles === 2 ? true : false;

        if (
          singleHandle &&
          (stileCategory?.includes("Slimline") ||
            isPico ||
            (hasBluetoothHandle && !isPico))
        ) {
          const lockStile =
            isPico &&
            isCorner &&
            primaryDoor === "Left" &&
            configChoice.includes("R")
              ? slimLockStile
              : isPico &&
                  isCorner &&
                  primaryDoor === "Right" &&
                  configChoice.includes("L")
                ? slimLockStile
                : isPico
                  ? slim
                  : hasBluetoothHandle
                    ? tradStile
                    : slimLockStile;

          innerStileWidth = pixelWidthPanel - lockStile - stileThickness;

          if (
            typeof handleObj === "object" &&
            handleObj?.handleSide === "Left"
          ) {
            stileRight = stileThickness;
            stileLeft = lockStile;
          }

          if (
            typeof handleObj === "object" &&
            handleObj?.handleSide === "Right"
          ) {
            stileRight = lockStile;
            stileLeft = stileThickness;
          }
        } else if (
          doubleHandle &&
          (stileCategory?.includes("Slimline") ||
            isPico ||
            (hasBluetoothHandle && !isPico))
        ) {
          const lockStile = hasBluetoothHandle
            ? tradStile
            : isPico
              ? slim
              : slimLockStile;
          if (
            typeof handleObj === "object" &&
            handleObj &&
            "panelWithHandleLeft" in handleObj &&
            i + 1 === handleObj?.panelWithHandleLeft
          ) {
            if (XSlider) {
              stileLeft = lockStile;
              stileRight = stileThickness;
            } else {
              stileLeft = stileThickness;
              stileRight = lockStile;
              innerStileWidth = pixelWidthPanel - stileRight - stileLeft;
            }
          } else if (
            typeof handleObj === "object" &&
            handleObj &&
            "panelWithHandleRight" in handleObj &&
            i + 1 === handleObj?.panelWithHandleRight
          ) {
            if (XSlider) {
              stileRight = lockStile;
              stileLeft = stileThickness;
            } else {
              stileRight = stileThickness;
              stileLeft = lockStile;
              innerStileWidth = pixelWidthPanel - stileRight - stileLeft;
            }
          } else {
            stileLeft = stileThickness;
            stileRight = stileThickness;
          }
        } else {
          if (isAluminum) {
            stileLeft = isSidelite ? alumSideliteThickness : alumFixedThickness;
            stileRight = isSidelite
              ? alumSideliteThickness
              : alumFixedThickness;
          } else if (isSidelite) {
            stileLeft = slim;
            stileRight = slim;
          } else {
            const picoCorner = isPico && isCorner;

            const lockStile =
              picoCorner &&
              primaryDoor === "Left" &&
              configChoice.includes("R") &&
              i === 0
                ? slimLockStile
                : picoCorner &&
                    primaryDoor === "Right" &&
                    configChoice.includes("L") &&
                    i + 1 === numberOfPanels
                  ? slimLockStile
                  : picoCorner &&
                      primaryDoor === "Right" &&
                      configChoice.includes("R") &&
                      i === 0
                    ? slim
                    : picoCorner &&
                        primaryDoor === "Left" &&
                        configChoice.includes("L") &&
                        i + 1 === numberOfPanels
                      ? slim
                      : stileThickness;

            stileLeft =
              picoCorner && configChoice.includes("R") && i === 0
                ? lockStile
                : stileThickness;

            stileRight =
              picoCorner &&
              configChoice.includes("L") &&
              i + 1 === numberOfPanels
                ? lockStile
                : stileThickness;
          }
        }

        // Draw and fill the glass section with the specified color
        ctx.fillStyle = "#e6fffb"; // Set fill color for the glass
        let glassPanelArgs: FillRectArgs;

        const glassX = xStart + panelWidthX + stileLeft;
        const glassY = yStart + railTop;

        if (isSlider) {
          const ovWidth = !stileCategory?.includes("Slimline")
            ? innerStileWidth - stileThickness
            : XSlider &&
                typeof handleObj === "object" &&
                handleObj &&
                "panelWithHandleRight" in handleObj &&
                i + 1 === handleObj?.panelWithHandleRight
              ? innerStileWidth - slimLockStile
              : singleHandle &&
                  typeof handleObj === "object" &&
                  i + 1 === handleObj?.panelWithHandle
                ? innerStileWidth
                : doubleHandle &&
                    typeof handleObj === "object" &&
                    handleObj &&
                    "panelWithHandleRight" in handleObj &&
                    "panelWithHandleLeft" in handleObj &&
                    (i + 1 === handleObj?.panelWithHandleRight ||
                      i + 1 === handleObj?.panelWithHandleLeft)
                  ? innerStileWidth
                  : innerStileWidth - stileThickness;

          innerStileWidth = ovWidth;

          glassPanelArgs = [glassX, glassY, ovWidth, innerStileHeight];
        } else {
          let width = 0;

          if (
            (isSwing || isPivot) &&
            sideLite !== "none" &&
            (sideliteSide === "left" || sideliteSide === "both") &&
            i === 1
          ) {
            width = panelWidthPixels - stileLeft - stileRight;
          } else if (isSidelite) {
            width = sideliteWidthPixels - stileLeft - stileRight;
          } else {
            width = panelWidthPixels - stileLeft - stileRight;
          }

          glassPanelArgs = [glassX, glassY, width, innerStileHeight];
        }

        if (!withPocket) {
          ctx.fillRect(...glassPanelArgs); // Fill inner rectangle
          ctx.strokeRect(...glassPanelArgs); // Draw inner rectangle border

          if (material === "All Wood") {
            ctx.save();
            ctx.fillStyle = fillStyle;

            if (!isRightCorner && !comboRightUnit) {
              const jambLegWoodArgs: FillRectArgs = [
                frameXStart,
                frameYStart,
                frame.jambLeg,
                frameHeightPixels,
              ];
              ctx.clearRect(...jambLegWoodArgs);
              ctx.fillRect(...jambLegWoodArgs);
            }

            const argsLeft: [number, number, number, number] = [
              xStart + panelWidthX,
              yStart,
              stileLeft,
              panelHeightPixels,
            ];

            const argsRight: [number, number, number, number] = [
              xStart + panelWidthX + panelArgs[2] - stileRight,
              yStart,
              stileRight,
              panelHeightPixels,
            ];

            if (isSlider) {
              const panelBeforeDash = configChoice.indexOf("-") - 1 === i;
              const panelAfterDash = configChoice.indexOf("-") === i;

              if (panelBeforeDash) {
                ctx.clearRect(...argsLeft);
                ctx.clearRect(...argsRight);
                ctx.fillRect(...argsLeft);
                ctx.fillRect(...argsRight);
              } else if (panelAfterDash) {
                ctx.clearRect(...argsLeft);
                ctx.fillRect(...argsLeft);
              } else if (
                i + 1 ===
                  configChoice.replace("P", "").replace("-", "").length ||
                i === numPanels - 1
              ) {
                ctx.clearRect(...argsLeft);
                ctx.fillRect(...argsLeft);
                if (configChoice[0] === "P" && !configChoice.includes("-")) {
                } else {
                  ctx.clearRect(...argsRight);
                  ctx.fillRect(...argsRight);
                }
              } else {
                ctx.clearRect(...argsLeft);
                ctx.fillRect(...argsLeft);
              }
            } else {
              ctx.clearRect(...argsLeft);
              ctx.clearRect(...argsRight);
              ctx.fillRect(...argsLeft);
              ctx.fillRect(...argsRight);
            }

            if (isCorner && cornerType === "Outside" && !isRightCorner) {
            } else {
              const widthToUse =
                isCorner && cornerType === "Inside" && !isRightCorner
                  ? (cornerFrameWidthLeft || 0) * scale
                  : isCorner && cornerType === "Inside" && isRightCorner
                    ? (cornerFrameWidthRight || 0) * scale
                    : hasSliderPocket && configChoice[0] === "P"
                      ? frameWidthPixels + pocketSize
                      : frameWidthPixels;

              const jamb = isRightCorner
                ? frameRightCorner.jambLeg
                : comboRightUnit === "Window"
                  ? frameWindowCombo.jambLeg
                  : hasSliderPocket &&
                      !configChoice.includes("-") &&
                      configChoice[0] === "P"
                    ? stileThickness
                    : frame.jambLeg;

              const doorHeightWoodArgs: FillRectArgs = [
                frameXStart + widthToUse - jamb,
                frameYStart,
                jamb,
                doorHeightPixels,
              ];

              ctx.clearRect(...doorHeightWoodArgs);
              ctx.fillRect(...doorHeightWoodArgs);
            }

            ctx.restore();

            ctx.save();
            ctx.fillStyle = railsWoodImg as CanvasPattern;

            const pocketSide = configChoice[0] === "P" ? "left" : "right";
            const headJambWoodArgs: FillRectArgs = [
              hasSliderPocket && pocketSide === "left"
                ? frameXStart + pocketSize
                : frameXStart,
              frameYStart,
              frameWidth,
              frame.headJamb,
            ];
            ctx.clearRect(...headJambWoodArgs);
            ctx.fillRect(...headJambWoodArgs);
            const railTopWoodArgs: FillRectArgs = [
              xStart + panelWidthX + stileLeft,
              yStart,
              glassPanelArgs[2],
              railTop,
            ];
            ctx.clearRect(...railTopWoodArgs);
            ctx.fillRect(...railTopWoodArgs);
            const railBtmWoodArgs: FillRectArgs = [
              xStart + panelWidthX + stileLeft,
              yStart + panelHeightPixels - railBtm,
              glassPanelArgs[2],
              railBtm,
            ];
            ctx.clearRect(...railBtmWoodArgs);
            ctx.fillRect(...railBtmWoodArgs);
            const sillWoodArgs: FillRectArgs = [
              hasSliderPocket && pocketSide === "left"
                ? frameXStart + pocketSize
                : frameXStart,
              yStart + panelHeightPixels,
              frameWidth,
              frame.sill,
            ];
            ctx.clearRect(...sillWoodArgs);
            ctx.fillRect(...sillWoodArgs);

            ctx.restore();
          }

          if (singleHandle) {
            drawHandle(
              typeof handleObj === "object" && handleObj?.handleSide
                ? handleObj.handleSide
                : "",
              i,
              panelWidthX,
              pixelWidthPanel,
            );
            drawDloDD(innerStileWidth, i);

            if (isPivot && pivotPoint && typeof handleObj === "object") {
              ctx.save();
              ctx.strokeStyle = "red";
              const pivotAxis = pivotPoint.includes("Middle")
                ? panelWidthPixels / 2
                : pivotPoint.split(" ")[0] === '4-3/4"'
                  ? 4.75 * scale
                  : Number(pivotPoint.split(" ")[0].replace('"', "")) * scale;
              let xPos = xStart + pivotAxis;

              if (sideliteSide === "left" || sideliteSide === "both") {
                xPos =
                  handleObj?.handleSide === "Right"
                    ? xStart + panelWidthX + pivotAxis
                    : xStart + panelWidthX + panelWidthPixels - pivotAxis;
              } else {
                xPos =
                  handleObj?.handleSide === "Right"
                    ? xStart + pivotAxis
                    : xStart + panelWidthPixels - pivotAxis;
              }
              ctx.save();
              ctx.beginPath();
              ctx.setLineDash([20, 10]);
              ctx.moveTo(xPos, yStart - 8 * scale);
              ctx.lineTo(xPos, yStart + panelHeightPixels + 8 * scale);
              ctx.stroke();
              ctx.restore();

              if (!pivotPoint.includes("Middle")) {
                ctx.save();
                ctx.strokeStyle = "black";
                ctx.fillStyle = "black";
                ctx.font = dataUrl ? "bold 12px Arial" : "10px Arial";
                let endPos =
                  handleObj?.handleSide === "Right"
                    ? xPos - pivotAxis
                    : xPos + pivotAxis;

                ctx.beginPath();
                ctx.moveTo(xPos, yStart - 8 * scale);
                ctx.lineTo(endPos, yStart - 8 * scale);
                ctx.stroke();

                const text = pivotPoint.includes("Middle")
                  ? "Middle"
                  : pivotPoint.split(" ")[0];
                ctx.fillText(
                  `${text} Axis`,
                  endPos < xPos ? endPos : xPos,
                  yStart - 9 * scale,
                );
                ctx.restore();
              }
            }
          } else if (doubleHandle) {
            if (
              typeof handleObj === "object" &&
              handleObj &&
              "panelWithHandleLeft" in handleObj &&
              i + 1 === handleObj?.panelWithHandleLeft
            ) {
              if ((isPico || isAluminum) && primaryDoor === "Right") {
              } else {
                drawHandle(
                  handleObj.handleSideLeft,
                  i,
                  panelWidthX,
                  pixelWidthPanel,
                );
              }

              if (primaryDoor === "Left") {
                drawDloDD(innerStileWidth, i);
              }
            } else if (
              typeof handleObj === "object" &&
              handleObj &&
              "panelWithHandleRight" in handleObj &&
              i + 1 === handleObj?.panelWithHandleRight
            ) {
              if ((isPico || isAluminum) && primaryDoor === "Left") {
              } else {
                drawHandle(
                  handleObj.handleSideRight,
                  i,
                  panelWidthX,
                  pixelWidthPanel,
                );
              }

              if (primaryDoor === "Right") {
                drawDloDD(innerStileWidth, i);
              }
            }
          }

          if (isBifold) {
            drawShootbolt(i, panelWidthX, pixelWidthPanel);
          }

          const alumFullbound =
            isAluminum &&
            !isBifoldDoor &&
            !isBifoldWindow &&
            (exactConfig?.includes("Full Bound") || doorType.includes("Window"))
              ? true
              : false;

          if (alumFullbound) {
            //add the rail lines
            ctx.save();

            ctx.lineWidth = 0.5;

            const linePlots: CanvasLinePlot[] = [
              {
                x1: frameXStart,
                y1: frameYStart,
                x2: glassPanelArgs[0],
                y2: yStart + railTop,
              },
              {
                x1: frameXStart,
                y1: frameYStart + frameHeightPixels,
                x2: glassPanelArgs[0],
                y2: yStart + panelHeightPixels - railBtm,
              },
              {
                x1: glassPanelArgs[0] + glassPanelArgs[2],
                y1: yStart + railTop,
                x2: frameXStart + frameWidthPixels,
                y2: frameYStart,
              },
              {
                x1: glassPanelArgs[0] + glassPanelArgs[2],
                y1: yStart + panelHeightPixels - railBtm,
                x2: frameXStart + frameWidthPixels,
                y2: frameYStart + frameHeightPixels,
              },
            ];

            linePlots.forEach((line, idx) => {
              if (idx === linePlots.length - 1) {
                ctx.lineWidth = 0.1;
              }
              drawLine(ctx, line.x1, line.y1, line.x2, line.y2);
            });
            ctx.restore();
          } else if (
            isAluminum &&
            (isSwing || isBifoldDoor || isBifoldWindow)
          ) {
            ctx.save();
            ctx.strokeStyle = strokeStyle;
            //add the mitered rail lines
            const topLeftStileMiter = isBifold
              ? true
              : (sideliteSide === "left" || sideliteSide === "both") &&
                  swingType === "Pair Swing" &&
                  i === 2
                ? true
                : !sideliteSide && swingType === "Pair Swing" && i === 1
                  ? true
                  : sideliteSide === "left" &&
                      swingType === "Single Swing" &&
                      exactConfig?.includes("RH") &&
                      swingDirection === "Outswing"
                    ? true
                    : false;

            const topRightStileMiter = isBifold
              ? true
              : (sideliteSide === "left" || sideliteSide === "both") &&
                  swingType === "Pair Swing" &&
                  i === 1
                ? true
                : !sideliteSide && swingType === "Pair Swing" && i === 0
                  ? true
                  : sideliteSide === "right" &&
                      swingType === "Single Swing" &&
                      exactConfig?.includes("LH") &&
                      swingDirection === "Outswing"
                    ? true
                    : false;

            const xTopLeftStart = xStart + panelWidthX;
            const xPosTopLeft = topLeftStileMiter
              ? xTopLeftStart
              : (sideliteSide === "left" || sideliteSide === "both") && i === 1
                ? xTopLeftStart - gap / 2
                : (sideliteSide === "right" || sideliteSide === "both") &&
                    i === numberOfPanels - 1
                  ? xTopLeftStart - gap / 2
                  : i === 0 || i === numberOfPanels - 1
                    ? xTopLeftStart - frame.jambLeg
                    : xTopLeftStart - gap;

            const btmStileMiter = isSidelite ? false : true;

            const xTopRightStart =
              glassPanelArgs[0] + glassPanelArgs[2] + stileRight;
            const xPosTopRight = topRightStileMiter
              ? xTopRightStart
              : (sideliteSide === "left" || sideliteSide === "both") && i === 1
                ? xTopRightStart + gap / 2
                : (sideliteSide === "right" || sideliteSide === "both") &&
                    i === numberOfPanels - 1
                  ? xTopRightStart + gap / 2
                  : i === 0 || i === numberOfPanels - 1
                    ? xTopRightStart + frame.jambLeg
                    : xTopRightStart + gap;

            const xBtmLeftStart = xStart + panelWidthX;
            const xPosBtmLeft = btmStileMiter
              ? xBtmLeftStart
              : (sideliteSide === "left" || sideliteSide === "both") && i === 1
                ? xBtmLeftStart - gap / 2
                : (sideliteSide === "right" || sideliteSide === "both") &&
                    i === numberOfPanels - 1
                  ? xBtmLeftStart - gap / 2
                  : i === 0 || i === numberOfPanels - 1
                    ? xBtmLeftStart - frame.jambLeg
                    : xBtmLeftStart - gap;

            const xBtmRightStart =
              glassPanelArgs[0] + glassPanelArgs[2] + stileRight;
            const xPosBtmRight = btmStileMiter
              ? xBtmRightStart
              : (sideliteSide === "left" || sideliteSide === "both") && i === 1
                ? xBtmRightStart + gap / 2
                : (sideliteSide === "right" || sideliteSide === "both") &&
                    i === numberOfPanels - 1
                  ? xBtmRightStart + gap / 2
                  : i === 0 || i === numberOfPanels - 1
                    ? xBtmRightStart + frame.jambLeg
                    : xBtmRightStart + gap;

            const heightOfPanel = isSidelite
              ? alumSideliteHeightPixels
              : panelHeightPixels;

            const linePlots: CanvasLinePlot[] = [
              //top left miter
              {
                x1: glassPanelArgs[0],
                y1: yStart + railTop,
                x2: xPosTopLeft,
                y2: topLeftStileMiter ? yStart : frameYStart,
              },
              //top right miter
              {
                x1: glassPanelArgs[0] + glassPanelArgs[2],
                y1: yStart + railTop,
                x2: xPosTopRight,
                y2: topRightStileMiter ? yStart : frameYStart,
              },
              //btm left miter
              {
                x1: glassPanelArgs[0],
                y1: yStart + heightOfPanel - railBtm,
                x2: xPosBtmLeft,
                y2:
                  yStart + panelHeightPixels + (btmStileMiter ? 0 : frame.sill),
              },
              //btm right miter
              {
                x1: glassPanelArgs[0] + glassPanelArgs[2],
                y1: yStart + heightOfPanel - railBtm,
                x2: xPosBtmRight,
                y2:
                  yStart + panelHeightPixels + (btmStileMiter ? 0 : frame.sill),
              },
            ];

            linePlots.forEach((line) => {
              drawLine(ctx, line.x1, line.y1, line.x2, line.y2);
            });
            ctx.restore();
          } else {
            //add the rail lines
            ctx.strokeStyle = strokeStyle;

            const linePlots: CanvasLinePlot[] = [
              {
                x1: glassPanelArgs[0],
                y1: yStart,
                x2: glassPanelArgs[0],
                y2: yStart + panelHeightPixels,
              },
              {
                x1: glassPanelArgs[0] + glassPanelArgs[2],
                y1: yStart,
                x2: glassPanelArgs[0] + glassPanelArgs[2],
                y2: yStart + panelHeightPixels,
              },
            ];

            linePlots.forEach((line) => {
              drawLine(ctx, line.x1, line.y1, line.x2, line.y2);
            });
          }

          drawHashMarks(isSidelite);
        }

        if (addDL && sdl && !withPocket) {
          let sdlChoice = sdl;
          if (isSidelite && diffSideliteSdl) {
            if (!dlLayoutSidelite) continue;
            sdlChoice = dlLayoutSidelite;
          }
          const wide = Number(sdlChoice[0]);
          const high = Number(sdlChoice[2]);

          ctx.save(); // Save the current state of the context
          const widthOfLite =
            liteConfig === '1 3/16"'
              ? 1.1875 * scale
              : liteConfig === '13/16"'
                ? 0.8125 * scale
                : liteConfig === '11/16"'
                  ? 0.6875 * scale
                  : liteConfig === '5 1/4"'
                    ? 5.25 * scale
                    : liteConfig === '2 1/2"'
                      ? 2.5 * scale
                      : liteConfig === '1 3/4"'
                        ? 1.75 * scale
                        : liteConfig === '1 1/8"'
                          ? 1.125 * scale
                          : liteConfig === '7/8"'
                            ? 0.875 * scale
                            : 0.625 * scale;
          ctx.lineWidth = widthOfLite;
          ctx.strokeStyle = fillStyle;

          const liteWidth =
            isAluminum && isSidelite
              ? sideliteWidthPixels - stileLeft - stileRight
              : isAluminum
                ? panelWidthPixels - stileLeft - stileRight
                : innerStileWidth;

          if (sdlChoice === "9 Lite Marginal") {
            const dloInt = fractionToInt(
              glass.glassWidth.split(" ")[0],
              glass.glassWidth.split(" ")[1] || "",
            );
            const horSpacing = (dloInt * scale) / 4;

            const x = xStart + panelWidthX + stileLeft;

            if (material === "All Wood") {
              ctx.strokeStyle = railsWoodImg as CanvasPattern;
            }

            for (let j = 0; j < 2; j++) {
              const space =
                j === 0
                  ? railTop + horSpacing
                  : railTop + innerStileHeight - horSpacing;
              const notch = yStart + space;
              drawLine(ctx, x, notch, x + liteWidth, notch);
            }

            ctx.strokeStyle = fillStyle;

            for (let j = 0; j < 3; j++) {
              if (j === 1) continue;
              const notch =
                xStart + panelWidthX + stileLeft + (j + 1) * horSpacing;
              drawLine(
                ctx,
                notch,
                yStart + railTop,
                notch,
                yStart + railTop + innerStileHeight,
              );
            }
          } else if (high === 1) {
            if (!daylight) continue;
            //Cottage Style
            const dlo = Number(daylight.replace('"', "")) * scale;

            const notch = yStart + railTop + dlo;

            const x = xStart + panelWidthX + stileLeft;

            if (material === "All Wood") {
              ctx.strokeStyle = railsWoodImg as CanvasPattern;
            }

            drawLine(ctx, x, notch, x + liteWidth, notch);

            ctx.strokeStyle = fillStyle;

            const vBars = wide - 1;
            const vGap = (liteWidth - vBars * widthOfLite) / wide;
            const xLeft = xStart + panelWidthX + stileLeft;

            for (let j = 0; j < vBars; j++) {
              const notch =
                xLeft + vGap + widthOfLite / 2 + j * (vGap + widthOfLite);
              drawLine(
                ctx,
                notch,
                yStart + railTop,
                notch,
                yStart + railTop + dlo,
              );
            }
          } else if (sdl === "3W2H") {
            if (!daylight) continue;
            //Cottage Style
            const dlo = Number(daylight.replace('"', "")) * scale;

            const notch = yStart + railTop + dlo;

            const x = xStart + panelWidthX + stileLeft;

            if (material === "All Wood") {
              ctx.strokeStyle = railsWoodImg as CanvasPattern;
            }

            drawLine(ctx, x, notch, x + liteWidth, notch);

            drawLine(ctx, x, notch - dlo / 2, x + liteWidth, notch - dlo / 2);

            ctx.strokeStyle = fillStyle;

            const vBars = wide - 1;
            const vGap = (liteWidth - vBars * widthOfLite) / wide;
            const xLeft = xStart + panelWidthX + stileLeft;

            for (let j = 0; j < vBars; j++) {
              const notchX =
                xLeft + vGap + widthOfLite / 2 + j * (vGap + widthOfLite);
              drawLine(
                ctx,
                notchX,
                yStart + railTop,
                notchX,
                yStart + railTop + dlo / 2,
              );
            }

            for (let j = 0; j < vBars; j++) {
              const notchX =
                xLeft + vGap + widthOfLite / 2 + j * (vGap + widthOfLite);
              drawLine(
                ctx,
                notchX,
                yStart + railTop + dlo / 2,
                notchX,
                yStart + railTop + dlo,
              );
            }
          } else {
            const bars = high - 1;
            const gap = (innerStileHeight - bars * widthOfLite) / high;

            const x = xStart + panelWidthX + stileLeft;
            const yTop = yStart + railTop;

            if (material === "All Wood") {
              ctx.strokeStyle = railsWoodImg as CanvasPattern;
            }

            if (canvasArg.isComboWindow && comboMatchHorizontalSdlBars) {
              // this is to make sure the horizantol bars line up on the door and window
              const doorGlassHeight = panelHeight * scale - railTop - railBtm;
              const doorGap = (doorGlassHeight - bars * widthOfLite) / high;
              for (let j = 0; j < bars; j++) {
                const notch =
                  yTop +
                  doorGap +
                  widthOfLite / 2 +
                  j * (doorGap + widthOfLite);
                if (notch >= yStart + railTop + innerStileHeight) break;
                drawLine(ctx, x, notch, x + liteWidth, notch);
              }
            } else {
              for (let j = 0; j < bars; j++) {
                const notch =
                  yTop + gap + widthOfLite / 2 + j * (gap + widthOfLite);
                drawLine(ctx, x, notch, x + liteWidth, notch);
              }
            }

            ctx.strokeStyle = fillStyle;

            const vBars = wide - 1;
            const vGap = (liteWidth - vBars * widthOfLite) / wide;
            const xLeft = xStart + panelWidthX + stileLeft;

            for (let j = 0; j < vBars; j++) {
              const notch =
                xLeft + vGap + widthOfLite / 2 + j * (vGap + widthOfLite);
              drawLine(
                ctx,
                notch,
                yStart + railTop,
                notch,
                yStart + railTop + innerStileHeight,
              );
            }
          }

          ctx.restore(); // Restore the saved state, reverting lineWidth and strokeStyle
        }
      }

      ctx.strokeStyle = "black";
      ctx.fillStyle = "black";

      // Draw overall dimension lines
      // Overall width
      if (hasSliderPocket) {
        const doubleSided = configChoice.includes("-");

        const startOfFrameY = frameYStart + frameHeightPixels + 50;
        const label = "DLF. Width";
        const frameWidthText = unitSizeFraction
          ? `${label}  ${unitSize} ${unitSizeFraction}"`
          : `${label}  ${unitSize}"`;

        const frameWidthTextPixels = getTextWidth(ctx, frameWidthText);

        const pocketSide = configChoice[0] === "P" ? "left" : "right";

        if (doubleSided) {
          const startOfFrameX = frameXStart + pocketSize;

          drawLine(
            ctx,
            startOfFrameX,
            startOfFrameY,
            startOfFrameX + frameWidthPixels,
            startOfFrameY,
          );
          ctx.fillText(
            frameWidthText,
            pocketSize + frameWidthPixels / 2 + frameWidthTextPixels / 2,
            startOfFrameY + 15,
          );
        } else {
          drawLine(
            ctx,
            pocketSide === "left" ? frameXStart + pocketSize : frameXStart,
            startOfFrameY,
            pocketSide === "left"
              ? frameXStart + pocketSize + frameWidthPixels
              : frameXStart + frameWidthPixels,
            startOfFrameY,
          );

          ctx.fillText(
            frameWidthText,
            (pocketSide === "left" ? pocketSize : 0) +
              frameWidthPixels / 2 +
              frameWidthTextPixels / 2,
            startOfFrameY + 15,
          );
        }

        const netWidthPixels =
          typeof pocketSlider === "object" && pocketSlider?.netWidth
            ? pocketSlider.netWidth * scale
            : 0;
        const netWidthStr = convertDecimalToFraction(
          typeof pocketSlider === "object" && pocketSlider?.netWidth
            ? pocketSlider.netWidth
            : 0,
        );

        const frameLineY = startOfFrameY + 25;

        drawLine(
          ctx,
          frameXStart,
          frameLineY,
          frameXStart + netWidthPixels,
          frameLineY,
        );
        ctx.font = font;
        const labelH = dataUrl ? "Net Frame Width" : "NFW";
        const unitSizeText = `${labelH}  ${netWidthStr}"`;
        const textWidthPixels = getTextWidth(ctx, unitSizeText);
        ctx.fillText(
          unitSizeText,
          frameXStart + netWidthPixels / 2 - textWidthPixels / 2,
          frameLineY + 15,
        );
      } else {
        const frameLineY = frameYStart + frameHeightPixels + 50;
        drawLine(
          ctx,
          frameXStart,
          frameLineY,
          frameXStart + frameWidth,
          frameLineY,
        );
        ctx.font = font;
        const label = dataUrl ? "Net Frame Width" : "NFW";
        const frameWidthText = `${label}  ${frameWidthStr}`;
        const textWidthPixels = getTextWidth(ctx, frameWidthText);
        const frameWidthYPos = frameLineY + 20;

        function formatWidthXPos(xStart: number) {
          return xStart + frameWidth / 2 - textWidthPixels / 2;
        }

        if (isRightCorner) {
          ctx.fillText(
            frameWidthText,
            formatWidthXPos(150 + doorWidthPixels),
            frameWidthYPos,
          );
        } else if (comboRightUnit) {
          ctx.fillText(
            frameWidthText,
            formatWidthXPos(frameXStart),
            frameWidthYPos,
          );
        } else {
          ctx.font = font;
          ctx.fillText(frameWidthText, formatWidthXPos(xStart), frameWidthYPos);
        }
      }

      const canvasLinePlots: CanvasLinePlot[] = [];
      const canvasHeightText: CanvasHeightTextPlot[] = [];
      const canvasWidthText: CanvasWidthTextPlot[] = [];

      const panelWidthTextOptions: [string, string] = [
        "Panel Width",
        "P.Width",
      ];
      const sideliteWidthTextOptions: [string, string] = [
        "Sidelite Width",
        "SL.Width",
      ];
      const dloTextOptions: [string, string] = ["DLO", "DLO"];

      // Overall height panel
      if (isCombo && !comboRightUnit) {
        ctx.font = font;

        const panelHeightStart = frameXStart - 40;

        // Overall height glass
        const xGlassHeight = frameXStart - 20;
        // Overall height frame
        const xHeight = frameXStart - 60;

        canvasLinePlots.push(
          //Panel Height Vertical line
          {
            x1: panelHeightStart,
            y1: yStart,
            x2: panelHeightStart,
            y2: yStart + panelHeightPixels,
          },
          //Glass Height Vertical Line
          {
            x1: xGlassHeight,
            y1: yStart + railTop,
            x2: xGlassHeight,
            y2: yStart + panelHeightPixels - railBtm,
          },
          //Frame Height Vertical Line
          {
            x1: xHeight,
            y1: frameYStart,
            x2: xHeight,
            y2: frameYStart + frameHeightPixels,
          },
        );

        //draw panel height text
        const panelHeightTextStartX = panelHeightStart - 5;
        const labelPH = dataUrl ? "Panel Height" : "P.Height";
        const panelHeightText = `${labelPH}  ${convertDecimalToFraction(
          panelHeightInt,
        )}"`;
        canvasHeightText.push({
          text: panelHeightText,
          x: panelHeightTextStartX,
          y: yStart + panelHeightPixels / 2,
        });
        const labelNH = dataUrl ? "Net Frame Height" : "NFH";

        const heightText = `${labelNH} ${frameHeightStr}`;

        //draw frame height text
        let frameHeightTextX = xHeight - 5;

        canvasHeightText.push(
          {
            text: "DLO  " + glassObj.glassHeight + '"',
            x: xGlassHeight - 5,
            y: yStart + (panelHeightPixels - railBtm) / 2,
          },
          {
            text: heightText,
            x: frameHeightTextX,
            y: frameYStart + frameHeightPixels / 2,
          },
        );
      } else if (!isRightCorner && !comboRightUnit) {
        ctx.font = font;

        const doubleSided = configChoice.includes("-");
        const panelHeightStart = xStart + 20 + frameWidthPixels;
        const addPocketSize =
          hasSliderPocket && doubleSided ? 2 : hasSliderPocket ? 1 : 0;
        const pocketAdder = pocketSize * addPocketSize;
        const xPanelHeight = panelHeightStart + pocketAdder;

        // Overall height glass
        const xGlassHeight = frameXStart - 20;
        // Overall height frame
        const panelHeightFrameStart = xStart + 50 + frameWidthPixels;
        const xHeight = panelHeightFrameStart + pocketAdder;

        canvasLinePlots.push(
          //Panel Height Vertical line
          {
            x1: xPanelHeight,
            y1: yStart,
            x2: xPanelHeight,
            y2: yStart + panelHeightPixels,
          },
          //Glass Height Vertical Line
          {
            x1: xGlassHeight,
            y1: yStart + railBtm,
            x2: xGlassHeight,
            y2: yStart + panelHeightPixels - railBtm,
          },
          //Frame Height Vertical Line
          {
            x1: xHeight,
            y1: frameYStart,
            x2: xHeight,
            y2: frameYStart + frameHeightPixels,
          },
        );

        //draw panel height text
        let panelHeightTextStartX = 85 + frameWidthPixels + distanceToMiddle;
        const labelPH = dataUrl ? "Panel Height" : "P.Height";
        const panelHeightText = `${labelPH}  ${convertDecimalToFraction(
          panelHeightInt,
        )}"`;
        canvasHeightText.push({
          text: panelHeightText,
          x: panelHeightTextStartX + pocketAdder,
          y: yStart + frameHeightPixels / 2,
        });
        const labelNH = dataUrl ? "Net Frame Height" : "NFH";

        const heightText = `${labelNH} ${frameHeightStr}`;

        if (isAlumSidelite) {
          // Overall sidelite height glass
          const slxGlassHeight = frameXStart - 50;
          canvasLinePlots.push(
            {
              x1: slxGlassHeight,
              y1: yStart + alumSideliteThickness,
              x2: slxGlassHeight,
              y2: yStart + alumSideliteHeightPixels - alumSideliteThickness,
            },
            {
              x1: slxGlassHeight - 20,
              y1: yStart + frame.headJamb,
              x2: slxGlassHeight - 20,
              y2: yStart + frame.headJamb + alumSideliteHeightPixels,
            },
          );

          //draw sidelite glass height text
          const labelDLO = dataUrl ? "Sidelite DLO " : "SL. DLO ";
          const labelHeight = dataUrl ? "Sidelite Height " : "SL. Height ";

          canvasHeightText.push(
            {
              text: labelDLO + glassAlumSidelite.glassHeight + '"',
              x: slxGlassHeight - 5,
              y: yStart + frameHeightPixels / 2,
            },
            {
              text:
                labelHeight +
                convertDecimalToFraction(alumSideliteHeight) +
                '"',
              x: slxGlassHeight - 25,
              y: yStart + frameHeightPixels / 2,
            },
          );
        }

        //draw frame height text
        let frameHeightTextX = 120 + frameWidthPixels + distanceToMiddle;

        canvasHeightText.push(
          {
            text: "DLO  " + glassObj.glassHeight + '"',
            x: frameXStart - 30,
            y: yStart + frameHeightPixels / 2,
          },
          {
            text: heightText,
            x: frameHeightTextX + pocketAdder,
            y: frameYStart + frameHeightPixels / 2,
          },
        );
      } else if (isRightCorner) {
        // Overall height glass
        const xGlassHeight = frameXStart - 20;
        canvasLinePlots.push({
          x1: xGlassHeight,
          y1: yStart + railBtm,
          x2: xGlassHeight,
          y2: yStart + panelHeightPixels - railBtm,
        });

        canvasHeightText.push({
          text: "DLO " + glassObj.glassHeight + '"',
          x: frameXStart - 30,
          y: yStart + frameHeightPixels / 2,
        });
      } else if (comboRightUnit) {
        // Overall height glass
        const xGlassHeight = frameXStart + frameWidthPixels + 20;
        canvasLinePlots.push({
          x1: xGlassHeight,
          y1: yStart + railBtm,
          x2: xGlassHeight,
          y2: yStart + panelHeightPixels - railBtm,
        });

        canvasHeightText.push({
          text: "DLO " + glassObj.glassHeight + '"',
          x: xGlassHeight - 5,
          y: yStart + frameHeightPixels / 2,
        });

        // panel height
        const xPanelHeight = frameXStart + frameWidthPixels + 40;
        canvasLinePlots.push({
          x1: xPanelHeight,
          y1: yStart,
          x2: xPanelHeight,
          y2: yStart + panelHeightPixels,
        });

        const labelPH = dataUrl ? "Panel Height" : "P.Height";
        const panelHeightText = `${labelPH}  ${convertDecimalToFraction(
          panelHeightInt,
        )}"`;

        canvasHeightText.push({
          text: panelHeightText,
          x: xPanelHeight - 5,
          y: yStart + panelHeightPixels / 2,
        });

        const labelNH = dataUrl ? "Net Frame Height" : "NFH";
        const heightText = `${labelNH} ${frameHeightStr}`;

        // frame height
        const xFrameHeight = frameXStart + frameWidthPixels + 60;
        canvasLinePlots.push({
          x1: xFrameHeight,
          y1: yStart,
          x2: xFrameHeight,
          y2: yStart + frameHeightPixels,
        });

        canvasHeightText.push({
          text: heightText,
          x: xFrameHeight - 5,
          y: yStart + frameHeightPixels / 2,
        });
      }

      canvasHeightText.forEach((label) => {
        drawHeightText(ctx, label.text, label.x, label.y);
      });

      if (isCombo && canvasArg.isComboWindow) {
        let startXPos = 0;

        if (comboRightUnit) {
          const distanceFromEdge = frameWidthPixels * 0.8;
          startXPos = frameXStart + distanceFromEdge;
        } else {
          const distanceFromEdge = frameWidthPixels * 0.2;
          startXPos = frameXStart + frameWidthPixels - distanceFromEdge;
        }

        const startYPos = frameYStart + frameHeightPixels;
        const endYPos = startYPos + doorHeightPixels - comboWindowHeightPixels;

        drawLine(ctx, startXPos, startYPos, startXPos, endYPos);

        const labelTxt = convertDecimalToFraction(
          netHeight - netHeightComboWindow,
        );

        drawHeightText(
          ctx,
          labelTxt + '"',
          startXPos - 5,
          startYPos + (doorHeightPixels - comboWindowHeightPixels) / 2,
        );
      }

      // Draw individual glass width
      const glassWidthY = frameYStart - 10;

      if (hasSliderPocket) {
        const innerStileWidth =
          panelWidthPixels - stileThickness - stileThickness;
        const start =
          configChoice[0] === "P"
            ? xStart + pocketSize + stileLeft
            : xStart + stileLeft;

        canvasLinePlots.push({
          x1: start,
          y1: glassWidthY,
          x2: start + innerStileWidth,
          y2: glassWidthY,
        });

        canvasWidthText.push({
          labelOptions: dloTextOptions,
          width: glassObj.glassWidth + '"',
          pixelWidth:
            configChoice[0] === "P"
              ? pocketSize * 2 + stileLeft * 2 + innerStileWidth
              : stileLeft * 2 + innerStileWidth,
          xStart,
          yPosTop: glassWidthY - 5 - lineHeight,
          yPosBtm: glassWidthY - 5,
        });
      } else {
        let innerStileWidth = isAluminum
          ? panelWidthPixels - alumFixedThickness - alumFixedThickness
          : panelWidthPixels - stileThickness - stileThickness;
        let start =
          isSlider && !hasSliderPocket && dloPanel
            ? xStart + (dloPanel || 0) * panelWidthPixels
            : isAluminum
              ? xStart + (dloPanel || 0) * panelWidthPixels + alumFixedThickness
              : xStart + (dloPanel || 0) * panelWidthPixels + stileThickness;

        let end = start + innerStileWidth;

        if (picoSboltPanels.length) {
          const sboltEqualsDLO = picoSboltPanels[0] === (dloPanel || 1);
          const endPanelIndex = picoSboltPanels.length - 1;
          const sboltPanel =
            sboltEqualsDLO && picoSboltPanels[endPanelIndex] === numPanels
              ? picoSboltPanels[endPanelIndex] - 1
              : sboltEqualsDLO && picoSboltPanels[endPanelIndex] < numPanels
                ? picoSboltPanels[endPanelIndex] + 1
                : isRightCorner
                  ? picoSboltPanels[0] + 1
                  : picoSboltPanels[0];
          let sboltStart = xStart + stileThickness;

          for (let i = 1; i < sboltPanel; i++) {
            sboltStart += picoSboltPanels.includes(i)
              ? panelWidthPicoShootboltPixels
              : panelWidthPixels;
          }

          let sboltEnd =
            sboltStart +
            (sboltEqualsDLO
              ? innerStileWidth
              : panelWidthPicoShootboltPixels -
                stileThickness -
                stileThickness);

          ctx.moveTo(sboltStart, glassWidthY);
          ctx.lineTo(sboltEnd, glassWidthY);
          ctx.stroke();

          const glassTxtLeft =
            sboltEqualsDLO && "glassWidthDD" in glassObj
              ? glassObj.glassWidthDD
              : glassObj.glassWidth;
          const xPosDlo = (sboltStart + sboltEnd) / 2 - 15;
          ctx.fillText("DLO", xPosDlo, glassWidthY - 20);
          ctx.fillText(glassTxtLeft + '"', xPosDlo, glassWidthY - 5);

          ctx.moveTo(start, glassWidthY);
          ctx.lineTo(end, glassWidthY);
          ctx.stroke();

          const glassTxtRight = sboltEqualsDLO
            ? glassObj.glassWidth
            : "glassWidthDD" in glassObj
              ? glassObj.glassWidthDD
              : glassObj.glassWidth;
          const xPosDlo2 = (start + end) / 2 - 15;
          ctx.fillText("DLO", xPosDlo2, glassWidthY - 20);
          ctx.fillText(glassTxtRight + '"', xPosDlo2, glassWidthY - 5);
        } else if (
          (isSwing || isPivot) &&
          exactConfig?.includes("Sidelite") &&
          (sideliteSide === "left" || sideliteSide === "both")
        ) {
          if (isAluminum) {
            innerStileWidth =
              sideliteWidthPixels -
              alumSideliteThickness -
              alumSideliteThickness;
            start = xStart + alumSideliteThickness;
          } else {
            innerStileWidth = sideliteWidthPixels - slim - slim;
            start = xStart + slim;
          }
          end = start + innerStileWidth;

          const dayLightsidelite = isAlumSidelite
            ? glassAlumSidelite.glassWidth
            : sideLiteWidth?.split(" ")[1]
              ? `${Number(sideLiteWidth?.split(" ")[0]) - 5} ${
                  sideLiteWidth?.split(" ")[1]
                }`
              : `${Number(sideLiteWidth?.split(" ")[0]) - 5}`;

          const gap = gaps[`panel2`];

          const xActiveStart = isAluminum
            ? end + gap + alumSideliteThickness + alumFixedThickness
            : end + gap + slim + stileThickness;
          const xActiveEnd = isAluminum
            ? xActiveStart +
              panelWidthPixels -
              alumFixedThickness -
              alumFixedThickness
            : xActiveStart + panelWidthPixels - stileThickness - stileThickness;

          canvasLinePlots.push(
            {
              x1: start,
              y1: glassWidthY,
              x2: end,
              y2: glassWidthY,
            },
            {
              x1: xActiveStart,
              y1: glassWidthY,
              x2: xActiveEnd,
              y2: glassWidthY,
            },
          );

          const yPosTop = glassWidthY - 5 - lineHeight;
          const yPosBtm = glassWidthY - 5;

          canvasWidthText.push(
            {
              labelOptions: dloTextOptions,
              width: dayLightsidelite + '"',
              pixelWidth: innerStileWidth,
              xStart: start,
              yPosTop,
              yPosBtm,
            },
            {
              labelOptions: dloTextOptions,
              width: glassObj.glassWidth + '"',
              pixelWidth: isAluminum
                ? panelWidthPixels - alumFixedThickness - alumFixedThickness
                : panelWidthPixels - stileThickness - stileThickness,
              xStart: xActiveStart,
              yPosTop,
              yPosBtm,
            },
          );
        } else if (
          (isSwing || isPivot) &&
          exactConfig?.includes("Sidelite") &&
          sideliteSide === "right"
        ) {
          innerStileWidth = panelWidthPixels - stileThickness - stileThickness;
          const dayLightsidelite = isAlumSidelite
            ? glassAlumSidelite.glassWidth
            : sideLiteWidth?.split(" ")[1]
              ? `${Number(sideLiteWidth.split(" ")[0]) - 5} ${
                  sideLiteWidth.split(" ")[1]
                }`
              : `${Number(sideLiteWidth?.split(" ")[0]) - 5}`;

          canvasLinePlots.push(
            {
              x1: xStart + panelWidthX + slim,
              y1: glassWidthY,
              x2:
                xStart + panelWidthX + slim + sideliteWidthPixels - slim - slim,
              y2: glassWidthY,
            },
            {
              x1: xStart + stileThickness,
              y1: glassWidthY,
              x2: xStart + stileThickness + innerStileWidth,
              y2: glassWidthY,
            },
          );

          const yPosTop = glassWidthY - 5 - lineHeight;
          const yPosBtm = glassWidthY - 5;

          canvasWidthText.push(
            {
              labelOptions: dloTextOptions,
              width: dayLightsidelite + '"',
              pixelWidth: sideliteWidthPixels - slim - slim,
              xStart: xStart + panelWidthX + slim,
              yPosTop,
              yPosBtm,
            },
            {
              labelOptions: dloTextOptions,
              width: glass.glassWidth + '"',
              pixelWidth: innerStileWidth,
              xStart: xStart + stileThickness,
              yPosTop,
              yPosBtm,
            },
          );
        } else {
          canvasLinePlots.push({
            x1: start,
            y1: glassWidthY,
            x2: end,
            y2: glassWidthY,
          });

          canvasWidthText.push({
            labelOptions: dloTextOptions,
            width: glassObj.glassWidth + '"',
            pixelWidth: innerStileWidth,
            xStart: start,
            yPosTop: glassWidthY - 5 - lineHeight,
            yPosBtm: glassWidthY - 5,
          });
        }
      }

      // Draw individual panel width dimension (for first panel only for simplicity)
      const panelWidthY = 15 + frameYStart + frameHeightPixels;
      const yPosTop = panelWidthY + 15;
      const yPosBtm = panelWidthY + 30;

      if (hasSliderPocket) {
        const pocketSide = configChoice[0] === "P" ? "left" : "right";

        const x = pocketSide === "left" ? frameXStart + pocketSize : xStart;
        const pocketX = frameXStart + pocketSize + frameWidth;

        canvasLinePlots.push(
          {
            x1: pocketSide === "left" ? frameXStart : pocketX,
            y1: panelWidthY,
            x2:
              pocketSide === "left"
                ? frameXStart + pocketSize
                : pocketX - pocketSize,
            y2: panelWidthY,
          },
          {
            x1: x,
            y1: panelWidthY,
            x2: x + panelWidthPixels,
            y2: panelWidthY,
          },
        );

        canvasWidthText.push(
          {
            labelOptions: ["Pocket", "Pocket"],
            width: pocketSize / scale,
            pixelWidth: pocketSize,
            xStart:
              pocketSide === "left" ? frameXStart : frameXStart + frameWidth,
            yPosTop,
            yPosBtm,
          },
          {
            labelOptions: panelWidthTextOptions,
            width: panelWidth,
            pixelWidth: panelWidthPixels,
            xStart: x,
            yPosTop,
            yPosBtm,
          },
        );
      } else {
        if (
          (isSwing || isPivot) &&
          exactConfig.includes("Sidelite") &&
          (sideliteSide === "left" || sideliteSide === "both")
        ) {
          const gap = gaps[`panel2`];

          const xActiveStart =
            sideliteSide === "both"
              ? xStart + sideliteWidthPixels + gap
              : xStart + panelWidthX;

          canvasLinePlots.push(
            {
              x1: xStart,
              y1: panelWidthY,
              x2: xStart + sideliteWidthPixels,
              y2: panelWidthY,
            },
            {
              x1: xActiveStart,
              y1: panelWidthY,
              x2: xActiveStart + panelWidthPixels,
              y2: panelWidthY,
            },
          );

          canvasWidthText.push(
            {
              labelOptions: sideliteWidthTextOptions,
              width: sideLiteWidth + '"',
              pixelWidth: sideliteWidthPixels,
              xStart,
              yPosTop,
              yPosBtm,
            },
            {
              labelOptions: panelWidthTextOptions,
              width: panelWidth,
              pixelWidth: panelWidthPixels,
              xStart: xActiveStart,
              yPosTop,
              yPosBtm,
            },
          );
        } else if (
          (isSwing || isPivot) &&
          exactConfig.includes("Sidelite") &&
          sideliteSide === "right"
        ) {
          const sideliteXStart = xStart + panelWidthX;

          canvasLinePlots.push(
            {
              x1: sideliteXStart,
              y1: panelWidthY,
              x2: sideliteXStart + sideliteWidthPixels,
              y2: panelWidthY,
            },
            {
              x1: xStart,
              y1: panelWidthY,
              x2: xStart + panelWidthPixels,
              y2: panelWidthY,
            },
          );
          canvasWidthText.push(
            {
              labelOptions: sideliteWidthTextOptions,
              width: sideLiteWidth + '"',
              pixelWidth: sideliteWidthPixels,
              xStart: sideliteXStart,
              yPosTop,
              yPosBtm,
            },
            {
              labelOptions: panelWidthTextOptions,
              width: panelWidth,
              pixelWidth: panelWidthPixels,
              xStart,
              yPosTop,
              yPosBtm,
            },
          );
        } else {
          if (picoSboltPanels.length) {
            const sboltFirstPanel = picoSboltPanels[0] === 1;

            const pixelWidthLeft = sboltFirstPanel
              ? panelWidthPicoShootboltPixels
              : panelWidthPixels;
            const pixelWidthRight = sboltFirstPanel
              ? panelWidthPixels
              : panelWidthPicoShootboltPixels;

            ctx.beginPath();
            ctx.moveTo(xStart, panelWidthY);
            ctx.lineTo(xStart + pixelWidthLeft, panelWidthY);
            ctx.stroke();

            const panelWidthTxtLeft = sboltFirstPanel
              ? convertDecimalToFraction(panelWidthInt + PICO_ADD)
              : convertDecimalToFraction(panelWidthInt);

            drawWidthText(
              ctx,
              panelWidthTextOptions,
              panelWidthTxtLeft,
              pixelWidthLeft,
              isRightCorner ? 150 + doorWidthPixels : xStart,
              panelWidthY + 15,
              panelWidthY + 30,
            );

            let sboltPixel = 0;
            const endPanelIndex = picoSboltPanels.length - 1;

            const sboltPanel =
              sboltFirstPanel && picoSboltPanels[endPanelIndex] === numPanels
                ? picoSboltPanels[endPanelIndex] - 1
                : sboltFirstPanel && picoSboltPanels[endPanelIndex] < numPanels
                  ? picoSboltPanels[endPanelIndex] + 1
                  : isRightCorner
                    ? picoSboltPanels[0] + 1
                    : picoSboltPanels[0];

            for (let i = 1; i < sboltPanel + 1; i++) {
              sboltPixel += picoSboltPanels.includes(i)
                ? panelWidthPicoShootboltPixels
                : panelWidthPixels;
            }

            ctx.beginPath();
            ctx.moveTo(xStart + sboltPixel - panelWidthPixels, panelWidthY);
            ctx.lineTo(xStart + sboltPixel, panelWidthY);
            ctx.stroke();
            if (isRightCorner || comboRightUnit) {
              const panelWidthTxtRight = sboltFirstPanel
                ? convertDecimalToFraction(panelWidthInt)
                : convertDecimalToFraction(panelWidthInt + PICO_ADD);

              const topWidthPixels = getTextWidth(ctx, "P.Width");
              const btmWidthPixels = getTextWidth(
                ctx,
                panelWidthTxtRight + '"',
              );

              const center = centerText(topWidthPixels, btmWidthPixels);

              const xPos = isRightCorner
                ? 150 + doorWidthPixels
                : comboRightUnit
                  ? xStart
                  : 0;
              const xPosText =
                xPos - topWidthPixels / 2 + sboltPixel - panelWidthPixels / 2;

              ctx.fillText("P.Width  ", xPosText, panelWidthY + 15);
              ctx.fillText(
                panelWidthTxtRight + '"',
                btmWidthPixels >= topWidthPixels
                  ? xPosText - center
                  : xPosText + center,
                panelWidthY + 30,
              );
            } else {
              const panelWidthTxtRight = sboltFirstPanel
                ? convertDecimalToFraction(panelWidthInt)
                : convertDecimalToFraction(panelWidthInt + PICO_ADD);

              const topWidthPixels = getTextWidth(ctx, "P.Width");
              const btmWidthPixels = getTextWidth(
                ctx,
                panelWidthTxtRight + '"',
              );

              const center = centerText(topWidthPixels, btmWidthPixels);

              const xPosPanel =
                xStart + sboltPixel - topWidthPixels / 2 - pixelWidthRight / 2;
              ctx.fillText("P.Width  ", xPosPanel, panelWidthY + 15);
              ctx.fillText(
                panelWidthTxtRight + '"',
                btmWidthPixels >= topWidthPixels
                  ? xPosPanel - center
                  : xPosPanel + center,
                panelWidthY + 30,
              );
            }
          } else {
            canvasLinePlots.push({
              x1: xStart,
              y1: panelWidthY,
              x2: xStart + panelWidthPixels,
              y2: panelWidthY,
            });
            if (isRightCorner) {
              canvasWidthText.push({
                labelOptions: panelWidthTextOptions,
                width: panelWidthInt,
                pixelWidth:
                  doorWidthPixels + frameWidthPixels + panelWidthPixels * 2,
                xStart: 150,
                yPosTop,
                yPosBtm,
              });
            } else {
              canvasWidthText.push({
                labelOptions: panelWidthTextOptions,
                width: panelWidthInt,
                pixelWidth: panelWidthPixels,
                xStart: xStart,
                yPosTop,
                yPosBtm,
              });
            }
          }
        }
      }

      canvasLinePlots.forEach((line) => {
        drawLine(ctx, line.x1, line.y1, line.x2, line.y2);
      });

      canvasWidthText.forEach((label) => {
        drawWidthText(
          ctx,
          label.labelOptions,
          label.width,
          label.pixelWidth,
          label.xStart,
          label.yPosTop,
          label.yPosBtm,
        );
      });

      //create overall net frame width line + label
      if (isCombo && finalUnit && firstUnitFrameXStart) {
        const doorUnitFrameYEnd =
          firstUnitFrameYEnd ?? frameYStart + frameHeightPixels;

        drawLine(
          ctx,
          firstUnitFrameXStart,
          doorUnitFrameYEnd + 90,
          frameXStart + frameWidth,
          doorUnitFrameYEnd + 90,
        );

        const overallFrameWidth = netWidth + netWidthComboWindow;
        const overallFrameWidthPixels =
          frameXStart - firstUnitFrameXStart + frameWidth;

        const labelH = dataUrl ? "Overall Net Frame Width" : "Overall NFW";
        const unitSizeText = `${labelH}  ${overallFrameWidth}"`;
        const textWidthPixels = getTextWidth(ctx, unitSizeText);
        ctx.fillText(
          unitSizeText,
          firstUnitFrameXStart +
            overallFrameWidthPixels / 2 -
            textWidthPixels / 2,
          doorUnitFrameYEnd + 105,
        );
      }
    }

    if (material === "All Wood") {
      var stilesWood = new Image();
      var railsWood = new Image();

      stilesWood.crossOrigin = "anonymous"; // Enable cross-origin
      railsWood.crossOrigin = "anonymous"; // Enable cross-origin

      const horizontalGrain = `${cloudinaryWoodURL2}/${woodImg.imgLabel}.jpg`;
      railsWood.src = horizontalGrain;
      const verticalGrain = `${cloudinaryWoodURL}/${woodImg.imgLabel}.jpg`;
      stilesWood.src = verticalGrain;

      var stilesPattern: CanvasPattern | null = null;
      var railsPattern: CanvasPattern | null = null;

      // Create a promise to handle the loading of both images
      Promise.all([
        new Promise<void>((resolve) => {
          stilesWood.onload = function () {
            stilesPattern = ctx.createPattern(
              this as CanvasImageSource,
              "repeat",
            );
            resolve();
          };
        }),
        new Promise<void>((resolve) => {
          railsWood.onload = function () {
            railsPattern = ctx.createPattern(
              this as CanvasImageSource,
              "repeat",
            );
            resolve();
          };
        }),
      ])
        .then(() => {
          if (isStaleDraw()) return;
          // Once both images are loaded, proceed with drawing
          panelWidthX = 0;
          canvasDraw(
            stilesPattern as CanvasPattern,
            railsPattern as CanvasPattern,
          );
        })
        .catch((error) => {
          console.error("Error loading images", error);
        });
    } else {
      panelWidthX = 0;
      canvasDraw(cladColor);
    }

    if (isCombo && !finalUnit && !isComboWindow) {
      return {
        frameXStart: frameXStart,
        frameYEnd: frameYStart + frameHeightPixels,
      };
    } else if (isCombo && !finalUnit && isComboWindow) {
      return { frameXStart: frameXStart, frameYEnd: null };
    } else {
      return null;
    }
  };

  useEffect(() => {
    const canvasElem = containerWindow ? canvasRefPortal : canvasRef;

    if (isInitialRender && containerWindow) {
      // Clear the canvas on first render in the portal
      clearCanvas(canvasElem);
    }

    drawIdRef.current += 1;

    if (!showPreview) return;
    if (hasErrors) return;

    const isComboWindow = isCombo && comboUnitLeft === "Window";

    const configChoice =
      isComboWindow && comboWindowConfig
        ? comboWindowConfig
        : exactConfig.includes("Primary")
          ? exactConfig.split(" ")[0]
          : exactConfig;

    const drawDoorObj: DrawDoor | null = drawDoor({
      frameWidthPixels: isComboWindow
        ? comboWindowWidthPixels
        : doorWidthPixels,
      frameHeightPixels: isComboWindow
        ? comboWindowHeightPixels
        : doorHeightPixels,
      frameWidthStr: isComboWindow
        ? formatDimStr(
            comboWindowUnitSize || "",
            comboWindowUnitSizeFraction || "",
          )
        : formatDimStr(unitSize, unitSizeFraction || ""),
      frameHeightStr: isComboWindow
        ? formatDimStr(comboWindowHeight || "", comboWindowHeightFraction || "")
        : formatDimStr(heightSize, heightFraction || ""),
      panelWidthInt:
        isAluminum && !isBifoldDoor && !isBifoldWindow
          ? panelWidthAlumPixels(
              scale,
              exactConfig,
              doorType,
              panelWidth,
              swingDirection || "",
              fullboundProfile || "",
              alumThickFrame || false,
            )
          : isComboWindow
            ? panelWidthWindowCombo
            : panelWidth,
      panelHeightInt:
        isAluminum && !isBifoldDoor && !isBifoldWindow
          ? panelHeightAlumPixels(
              scale,
              configChoice,
              doorType,
              sillStyle || "",
              netHeight,
              swingDirection || "",
              fullboundProfile || "",
              alumThickFrame || false,
            )
          : isComboWindow
            ? panelHeightWindowCombo
            : panelHeight,
      glassObj: isComboWindow ? glassWindowCombo : glass,
      configChoice,
      numberOfPanels: isComboWindow ? numPanelsComboWindow : numPanels,
      stileCategory: stiles || "",
      isRightCorner: false,
      comboRightUnit: "",
      isComboWindow,
      canvasElem,
    });

    if (isCorner) {
      drawDoor({
        frameWidthPixels: netWidthCornerRight * scale,
        frameHeightPixels: doorHeightPixels,
        frameWidthStr: formatDimStr(
          cornerWidthRight || "",
          cornerWidthFractionRight || "",
        ),
        frameHeightStr: formatDimStr(heightSize, heightFraction || ""),
        panelWidthInt: panelWidthRightCorner,
        panelHeightInt: panelHeight,
        glassObj: glassRightCorner,
        configChoice: cornerConfigRight || "",
        numberOfPanels: numPanelsRightCorner || 0,
        stileCategory: stiles || "",
        isRightCorner: true,
        comboRightUnit: "",
        canvasElem,
      });
    } else if (isCombo) {
      const isComboWindow = comboUnitRight === "Window";
      const frameXStart = drawDoorObj?.frameXStart;
      const frameYEnd = drawDoorObj?.frameYEnd;
      drawDoor({
        frameWidthPixels: isComboWindow
          ? comboWindowWidthPixels
          : doorWidthPixels,
        frameHeightPixels: isComboWindow
          ? comboWindowHeightPixels
          : doorHeightPixels,
        frameWidthStr: isComboWindow
          ? formatDimStr(
              comboWindowUnitSize || "",
              comboWindowUnitSizeFraction || "",
            )
          : formatDimStr(unitSize, unitSizeFraction || ""),
        frameHeightStr: isComboWindow
          ? formatDimStr(
              comboWindowHeight || "",
              comboWindowHeightFraction || "",
            )
          : formatDimStr(heightSize, heightFraction || ""),
        panelWidthInt: isComboWindow ? panelWidthWindowCombo : panelWidth,
        panelHeightInt: isComboWindow ? panelHeightWindowCombo : panelHeight,
        glassObj: isComboWindow ? glassWindowCombo : glass,
        configChoice:
          isComboWindow && comboWindowConfig ? comboWindowConfig : exactConfig,
        numberOfPanels: isComboWindow ? numPanelsComboWindow : numPanels,
        stileCategory: stiles || "",
        isRightCorner: false,
        comboRightUnit: comboUnitRight || "",
        isComboWindow,
        canvasElem,
        firstUnitFrameXStart: frameXStart,
        firstUnitFrameYEnd: frameYEnd,
        finalUnit: true,
      });
    }
  }, [
    canvasWidth,
    canvasHeight,
    doorWidthPixels,
    doorHeightPixels,
    panelWidth,
    comboWindowWidthPixels,
    comboWindowHeightPixels,
    comboWindowConfig,
    comboUnitLeft,
    // swingPanelWidth,
    // swingPanelFraction,
    sideLiteWidth,
    exactConfig,
    exactConfigImg,
    cornerConfigRight,
    netWidthCornerRight,
    sillStyle,
    isComboSillDifferent,
    comboWindowSillStyle,
    stilesAndRails,
    stiles,
    sdl,
    comboMatchHorizontalSdlBars,
    liteConfig,
    daylight,
    exteriorCladColor,
    aluminumColorExterior,
    material,
    woodSpecies,
    handleHeight,
    handleStyle,
    handleColor,
    hasHandle,
    shootBoltColor,
    pivotPoint,
    pivotBackPlate,
    pivotBackPlateColor,
    cornerFrameWidthLeft,
    cornerFrameWidthRight,
    addScreen,
    hasErrors,
    diffSideliteSdl,
    dlLayoutSidelite,
    primaryDoor,
    fullboundProfile,
    withExtSbolt,
  ]);

  if (!showPreview) {
    return (
      <StyledDiv>
        <Typography style={{ color: "black" }}>
          Missing Required Fields:
        </Typography>
        <ul>
          {material === "All Wood" && !woodSpecies ? (
            <li style={{ color: "red" }}>Wood Species</li>
          ) : null}
          {isCorner && (!unitSize || !cornerWidthRight) ? (
            <li style={{ color: "red" }}>Unit Size</li>
          ) : isCombo && (!unitSize || !comboWindowUnitSize) ? (
            <li style={{ color: "red" }}>Unit Size</li>
          ) : !doorWidthPixels ? (
            <li style={{ color: "red" }}>Unit Size</li>
          ) : null}
          {(isSwing || isPivot) && !swingPanelWidth ? (
            <li style={{ color: "red" }}>Panel Width</li>
          ) : null}
          {isCombo && (!doorHeightPixels || !comboWindowHeightPixels) ? (
            <li style={{ color: "red" }}>Height</li>
          ) : !doorHeightPixels ? (
            <li style={{ color: "red" }}>Height</li>
          ) : null}
          {isCorner && !cornerConfigRight ? (
            <li style={{ color: "red" }}>Configuration</li>
          ) : isCombo && !comboWindowConfig ? (
            <li style={{ color: "red" }}>Configuration</li>
          ) : !exactConfig ? (
            <li style={{ color: "red" }}>Configuration</li>
          ) : null}
          {!stilesAndRails && (
            <li style={{ color: "red" }}>Stiles and Rails</li>
          )}
        </ul>
      </StyledDiv>
    );
  }

  if (hasErrors) {
    return (
      <StyledDiv>
        <Typography style={{ color: "black" }}>
          Fix the Following Errors:
        </Typography>
        <ul>
          {errors.widthErr && (
            <li style={{ color: "red" }}>NFW: {errors.widthErr}</li>
          )}
          {errors.widthErrComboWindow && (
            <li style={{ color: "red" }}>
              NFW Window: {errors.widthErrComboWindow}
            </li>
          )}
          {isCorner && errors.widthErrCornerRight && (
            <li style={{ color: "red" }}>NFW: {errors.widthErrCornerRight}</li>
          )}
          {isCombo && errors.widthErrComboWindow && (
            <li style={{ color: "red" }}>NFW: {errors.widthErrComboWindow}</li>
          )}
          {errors.heightErr && (
            <li style={{ color: "red" }}>{errors.heightErr}</li>
          )}
          {isCombo && errors.heightErrComboWindow && (
            <li style={{ color: "red" }}>{errors.heightErrComboWindow}</li>
          )}
          {(isSwing || isPivot) && errors.swingWidthErr && (
            <li style={{ color: "red" }}>
              Panel Width: {errors.swingWidthErr}
            </li>
          )}
          {(isSwing || isPivot) && errors.sideliteWidthErr && (
            <li style={{ color: "red" }}>{errors.sideliteWidthErr}</li>
          )}
        </ul>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      {containerWindow ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: 10,
            }}
          >
            <Typography variant="caption" style={{ fontSize: 10 }}>
              As viewed from exterior
            </Typography>
            <Typography variant="caption" style={{ fontSize: 10 }}>
              All proportions and finishes are best approximations
            </Typography>
          </div>

          <canvas
            ref={canvasRefPortal}
            width={`${canvasWidth}`}
            height={`${canvasHeight}`}
          />
        </>
      ) : (
        <canvas
          ref={canvasRef}
          width={`${
            dataUrl && netHeight >= netWidth ? canvasWidth - 200 : canvasWidth
          }`}
          height={`${
            dataUrl && netHeight >= netWidth ? canvasHeight - 200 : canvasHeight
          }`}
        />
      )}
    </StyledDiv>
  );
};

export default CanvasBox;
